import { useState, useMemo, useEffect, useRef } from "react";
import * as XLSX from "xlsx";
import brand from "./brand";

// ── Excel export ─────────────────────────────────────────────────────────────

function buildSheet(rows, colWidths) {
  const ws = XLSX.utils.json_to_sheet(rows);
  ws["!cols"] = colWidths.map(w => ({ wch: w }));
  ws["!freeze"] = { xSplit: 0, ySplit: 1, topLeftCell: "A2", activePane: "bottomLeft", state: "frozen" };
  if (ws["!ref"]) ws["!autofilter"] = { ref: ws["!ref"] };
  return ws;
}

function downloadXLSX(sheets, filename) {
  const wb = XLSX.utils.book_new();
  sheets.forEach(([name, ws]) => XLSX.utils.book_append_sheet(wb, ws, name));
  XLSX.writeFile(wb, filename);
}

// ── Config ────────────────────────────────────────────────────────────────────

const API_BASE = "";

// ── Helpers ───────────────────────────────────────────────────────────────────

function today() { return new Date().toISOString().split("T")[0]; }
function fmtDate(iso) { if (!iso) return "—"; return iso.replace(/-/g, '.'); }

function useLocalStorage(key, initial) {
  const [value, setValue] = useState(() => {
    try {
      const stored = localStorage.getItem(key);
      return stored !== null ? JSON.parse(stored) : initial;
    } catch { return initial; }
  });
  function set(v) {
    if (typeof v === "function") {
      setValue(prev => {
        const next = v(prev);
        localStorage.setItem(key, JSON.stringify(next));
        return next;
      });
    } else {
      setValue(v);
      localStorage.setItem(key, JSON.stringify(v));
    }
  }
  return [value, set];
}

function useServerStorage(key, initial, loggedIn) {
  const [value, setValue] = useState(() => {
    try { const s = localStorage.getItem(key); return s !== null ? JSON.parse(s) : initial; }
    catch { return initial; }
  });
  const ref = useRef(value);
  ref.current = value;

  function refresh() {
    apiFetch(`/api/store/${key}`)
      .then(r => r.json())
      .then(r => {
        if (r.data != null) {
          const saved = parseInt(localStorage.getItem(`${key}_ts_saved`) || "0", 10);
          const modified = parseInt(localStorage.getItem(`${key}_ts_modified`) || "0", 10);
          if (modified <= saved) {
            setValue(r.data);
            localStorage.setItem(key, JSON.stringify(r.data));
          }
        }
      })
      .catch(() => {});
  }

  useEffect(() => {
    if (!loggedIn) return;
    refresh();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn]);

  function set(v) {
    const next = typeof v === "function" ? v(ref.current) : v;
    setValue(next);
    localStorage.setItem(key, JSON.stringify(next));
    const ts = Date.now();
    localStorage.setItem(`${key}_ts_modified`, String(ts));
    (async () => {
      for (let i = 0; i < 3; i++) {
        try {
          const res = await apiFetch(`/api/store/${key}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ data: next }) });
          if (res.ok) { localStorage.setItem(`${key}_ts_saved`, String(ts)); return; }
        } catch (_) {}
        if (i < 2) await new Promise(r => setTimeout(r, 1500 * (i + 1)));
      }
    })();
  }
  return [value, set, refresh];
}

const SHIPPING_COST = 560;
const DISCOUNT_PRODUCT_NAMES = new Set(['Dizajn: Popust na prvu porudžbinu']);
const FREE_SHIPPING_THRESHOLD = 7000;

function pdvFromPrice(price) { return price * 20 / 120; }
function totalProdCost(purchasePrice, costs) {
  return purchasePrice + Object.values(costs).reduce((s, v) => s + (parseFloat(v) || 0), 0);
}
function calcItemProfit(prodajnaCena, purchasePrice, costs) {
  const upc = totalProdCost(purchasePrice, costs);
  const dobitPdv = prodajnaCena - upc;
  const pdv = pdvFromPrice(prodajnaCena);
  const netoDobit = dobitPdv - pdv;
  const pctZarade = prodajnaCena > 0 ? (netoDobit / prodajnaCena) * 100 : 0;
  return { upc, dobitPdv, pdv, netoDobit, pctZarade };
}
function calcOrderProfit(order, prodCosts, purchasePrices, marketingEntries, allOrders, cfg = {}) {
  const shippingCost = cfg.shippingCost ?? SHIPPING_COST;
  const freeShippingThreshold = order.shippingThreshold ?? cfg.freeShippingThreshold ?? FREE_SHIPPING_THRESHOLD;
  if (!order.lines || order.lines.length === 0) {
    if (order.type === "print") {
      const pdv = pdvFromPrice(order.total || 0);
      const netoDobit = (order.total || 0) - pdv;
      return { netoDobit, pdv, pctZarade: 0, promo: 0, shipping: 0 };
    }
    return { netoDobit: 0, pdv: 0, pctZarade: 0, promo: 0, shipping: 0 };
  }
  const ordersOnDay = allOrders.filter(o => o.date === order.date && o.status !== "cancelled" && o.type === "web");
  const dailyMkt = marketingEntries.filter(e => e.date === order.date).reduce((s, e) => s + e.amount, 0);
  const promo = (order.type === "web" && ordersOnDay.length > 0) ? dailyMkt / ordersOnDay.length : 0;
  const lineTotal = order.lines.reduce((s, l) => s + (l.price || 0) * (l.qty || 0), 0);
  const discountScale = lineTotal > 0 && order.total < lineTotal ? order.total / lineTotal : 1;
  let totalNeto = 0, totalPdv = 0, totalProdajna = 0;
  order.lines.forEach(l => {
    const effectivePrice = l.price * discountScale;
    const pp = getPurchasePrice(l.product, l.color, purchasePrices, order.date);
    for (let i = 0; i < l.qty; i++) {
      const applicableCosts = SIMPLE_PRODUCTS.includes(l.product) ? {} : PRODUCT_COST_KEYS[l.product] ? Object.fromEntries(Object.entries(prodCosts).filter(([k]) => PRODUCT_COST_KEYS[l.product].includes(k))) : prodCosts;
      const r = calcItemProfit(effectivePrice, pp, applicableCosts);
      totalNeto += r.netoDobit;
      totalPdv += r.pdv;
      totalProdajna += effectivePrice;
    }
  });
  const shipping = (!cfg.noShipping && order.total >= freeShippingThreshold) ? shippingCost : 0;
  const netoDobit = totalNeto - promo - shipping;
  return { netoDobit, pdv: totalPdv, pctZarade: totalProdajna > 0 ? (netoDobit / totalProdajna) * 100 : 0, promo, shipping };
}

function extractDesignName(title) {
  let name = title;
  // Split off " | Mashina ..." brand descriptor
  if (name.includes(" | ")) name = name.split(" | ")[0].trim();
  // Strip trailing color " - Color"
  name = name.replace(/\s*[-–]\s*(bela|crna|natural|khaki|svetl[ao]\s+grafit|maslinasto\s+zelena|svetlo\s+plava|pepeljasto\s+teget|pepeljasto\s+siva|pepeljasto\s+zelena|svetlo\s+zelena|melirana\s+crvena|burgundi|nebo\s+plava|teget)\s*$/i, "").trim();
  // If product type is at the start, design name follows the separator after it
  const leadingType = name.match(/^(longsleeve\s+(retro\s+)?majica?)\s*[-–]\s*/i);
  if (leadingType) return name.slice(leadingType[0].length).trim() || title;
  // Strip product type keyword and everything after it (including "Retro Majica" patterns)
  const typeMatch = name.match(/\s*[-–]?\s*(retro\s+)?(longsleeve|majici|majica|duks|ceger|pakovanje)\b.*/i);
  if (typeMatch && typeMatch.index > 2) name = name.slice(0, typeMatch.index).trim();
  // Strip trailing " – Mashina..." branding
  name = name.replace(/\s*–\s*Mashina.*$/i, "").trim();
  // Strip trailing separators
  name = name.replace(/\s*[–-]\s*$/, "").trim();
  return name || title;
}

function inferProductCategory(name) {
  const n = name.toLowerCase();
  if (n.includes("longsleeve")) return "Longsleeve";
  if (n.includes("duks")) return "Dukserica";
  if (n.includes("ceger")) return "Ceger";
  if (n.includes("poklon pakovanje") || n.includes("gift")) return "Poklon pakovanje";
  return "Majica";
}

function normalizeColor(raw) {
  if (!raw) return "";
  const map = {
    "bela": "Bela", "crna": "Crna", "natural": "Natural",
    "khaki": "Khaki", "svetla grafit": "Svetlo Grafit", "svetlo grafit": "Svetlo Grafit",
    "maslinasto zelena": "Maslinasto zelena", "svetlo plava": "Svetlo plava",
    "pepeljasto teget": "Pepeljasto teget", "pepeljasto siva": "Pepeljasto teget",
    "pepeljasto zelena": "Pepeljasto teget", "svetlo zelena": "Svetlo zelena",
    "melirana crvena": "Melirana crvena", "burgundi": "Burgundi",
    "natural russel athletic": "Natural",
    "nebo plava": "Svetlo plava", "teget": "Pepeljasto teget",
  };
  const normalized = raw.toLowerCase().replace(/-/g, " ");
  return map[normalized] || raw;
}

const KNOWN_PRODUCT_PATTERNS = /longsleeve|duks|ceger|poklon\s+pakovanje|gift/i;

async function fetchDirectOrders() {
  try {
    const res = await apiFetch(`${API_BASE}/api/direct-orders`);
    const data = await res.json();
    return data.success ? data.orders : [];
  } catch { return []; }
}

let _pushChain = Promise.resolve();
async function pushDirectOrder(order) {
  _pushChain = _pushChain.then(async () => {
    for (let i = 0; i < 3; i++) {
      try {
        const res = await apiFetch(`${API_BASE}/api/direct-orders`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(order),
        });
        if (res.ok) return;
      } catch (_) {}
      if (i < 2) await new Promise(r => setTimeout(r, 1500 * (i + 1)));
    }
  });
}

async function removeDirectOrder(id) {
  try {
    await apiFetch(`${API_BASE}/api/direct-orders/${encodeURIComponent(id)}`, { method: "DELETE" });
  } catch (_) {}
}

async function fetchPrintStatuses() {
  try {
    const res = await apiFetch(`${API_BASE}/api/print-statuses`);
    const data = await res.json();
    return data.success ? data.statuses : {};
  } catch { return {}; }
}

async function pushPrintStatus(key, status) {
  try {
    await apiFetch(`${API_BASE}/api/print-statuses`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ key, status }),
    });
  } catch (_) {}
}

async function fetchServerStatuses() {
  try {
    const res = await apiFetch(`${API_BASE}/api/order-statuses`);
    const data = await res.json();
    return data.success ? data.statuses : {};
  } catch { return {}; }
}

async function fetchSentDates() {
  try {
    const res = await apiFetch(`${API_BASE}/api/sent-dates`);
    const data = await res.json();
    if (!data.success) return {};
    const d = data.dates;
    return (d && !Array.isArray(d)) ? d : {};
  } catch { return {}; }
}

let _sentDateChain = Promise.resolve();
async function pushSentDate(id, date) {
  _sentDateChain = _sentDateChain.then(async () => {
    for (let i = 0; i < 3; i++) {
      try {
        const res = await apiFetch(`${API_BASE}/api/sent-dates`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id, date }),
        });
        if (res.ok) return;
      } catch (_) {}
      if (i < 2) await new Promise(r => setTimeout(r, 1500 * (i + 1)));
    }
  });
}

async function fetchZoneOverrides() {
  try {
    const res = await apiFetch(`${API_BASE}/api/zone-overrides`);
    const data = await res.json();
    if (!data.success) return {};
    const z = data.zones;
    return (z && !Array.isArray(z)) ? z : {};
  } catch { return {}; }
}

let _zoneChain = Promise.resolve();
async function pushZoneOverride(id, zone) {
  _zoneChain = _zoneChain.then(async () => {
    for (let i = 0; i < 3; i++) {
      try {
        const res = await apiFetch(`${API_BASE}/api/zone-overrides`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id, zone }),
        });
        if (res.ok) return;
      } catch (_) {}
      if (i < 2) await new Promise(r => setTimeout(r, 1500 * (i + 1)));
    }
  });
}

let _statusChain = Promise.resolve();
async function pushStatusToServer(id, status) {
  _statusChain = _statusChain.then(async () => {
    for (let i = 0; i < 3; i++) {
      try {
        const res = await apiFetch(`${API_BASE}/api/order-statuses`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id, status }),
        });
        if (res.ok) return;
      } catch (_) {}
      if (i < 2) await new Promise(r => setTimeout(r, 1500 * (i + 1)));
    }
  });
}

function normalizeWooOrders(raw) {
  const unknownProducts = new Set();
  const orders = raw
    .filter(o => o.status !== "pending")
    .map(o => ({
      ...o,
      lines: o.lines
        .filter(l => !DISCOUNT_PRODUCT_NAMES.has(l.product))
        .map(l => ({
          ...l,
          dizajn: extractDesignName(l.product),
          product: inferProductCategory(l.product),
          color: normalizeColor(l.color),
          size: l.size ? l.size.toUpperCase() : l.size,
          _rawProduct: l.product,
        })),
    }));
  orders.forEach(o => o.lines.forEach(l => {
    const raw = (l._rawProduct || "").toLowerCase();
    if (!KNOWN_PRODUCT_PATTERNS.test(raw) && !raw.includes("majic") && !raw.includes("poklon")) {
      if (l.dizajn && l.dizajn !== l._rawProduct) return;
      unknownProducts.add(l._rawProduct);
    }
  }));
  return { orders, unknownProducts: [...unknownProducts] };
}

async function fetchWooOrders() {
  const res = await apiFetch(`${API_BASE}/api/orders`);
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
  return normalizeWooOrders(data.orders);
}

// ── Constants ─────────────────────────────────────────────────────────────────

const SIMPLE_PRODUCTS = ["Poklon pakovanje"];
const NO_VARIANTS = ["Poklon pakovanje", "Ceger"];
const PRODUCT_COST_KEYS = {
  "Ceger": ["Slika", "Tag", "Nalepnica", "Pakovanje"],
};
function getApplicableCosts(product, prodCosts) {
  if (SIMPLE_PRODUCTS.includes(product)) return {};
  if (PRODUCT_COST_KEYS[product]) return Object.fromEntries(Object.entries(prodCosts).filter(([k]) => PRODUCT_COST_KEYS[product].includes(k)));
  return prodCosts;
}

const PRODUCT_CATALOG = {
  Majica: ["Bela","Crna","Natural","Khaki","Svetlo Grafit","Maslinasto zelena","Svetlo plava","Pepeljasto teget","Svetlo zelena","Melirana crvena","Burgundi"],
  Dukserica: ["Bela","Crna","Natural","Khaki","Svetlo Grafit","Maslinasto zelena","Svetlo plava","Pepeljasto teget","Svetlo zelena","Melirana crvena"],
  "Longsleeve": ["Bela","Crna","Natural","Khaki","Svetlo Grafit","Maslinasto zelena","Svetlo plava","Pepeljasto teget","Svetlo zelena","Melirana crvena","Burgundi"],
  Ceger: ["Bez boje"],
  "Poklon pakovanje": ["Bez boje"],
};

const SIZES = ["S","M","L","XL","XXL","3XL"];

const DESIGN_LINKS_MASHINA = {
  Majica: {
    "Choose Your Waveform":               "https://www.mashina.rs/artikal/choose-your-waveform-majica/",
    "Analog Synth":                        "https://www.mashina.rs/artikal/analog-synth-majica/",
    "Quentin – I Didn't Create This Situation": "https://www.mashina.rs/artikal/quentin-i-didnt-create-this-situation-majica/",
    "Drift King – Toyota AE86":            "https://www.mashina.rs/artikal/drift-king-toyota-ae86-majica/",
    "Born To Cruise – Retro Chevrolet 1957": "https://www.mashina.rs/artikal/premium-retro-majica-chevrolet-1957/",
    "Italian Legend – Vespa":              "https://www.mashina.rs/artikal/vespa-majica-italian-legend/",
    "Sound Of The Streets":                "https://www.mashina.rs/artikal/sound-of-the-streets-retro-kasetofon/",
    "Stojadin":                            "https://www.mashina.rs/artikal/retro-majica-stojadin-ikona-domacih-puteva/",
    "Smooth Criminal Move":                "https://www.mashina.rs/artikal/smooth-criminal-move/",
    "BE COLD":                             "https://www.mashina.rs/artikal/be-cold-majica-mehanicko-srce/",
    "Rock N' Roll Vitruvian":              "https://www.mashina.rs/artikal/rock-n-roll-vitruvian/",
    "Mićko – Rođen za krivine":            "https://www.mashina.rs/artikal/micko-rodjen-za-krivine/",
    "North. South. East. West. Shoot.":    "https://www.mashina.rs/artikal/north-south-east-west-shoot-retro-majica/",
    "Williams Typewriter":                 "https://www.mashina.rs/artikal/williams-typewriter-retro-majica/",
  },
  Longsleeve: {
    "Pontiac GTO – Engine Over Hype":      "https://www.mashina.rs/artikal/pontiac-gto-engine-over-hype-longsleeve/",
    "Fićko! Rođen da bude klasik":         "https://www.mashina.rs/artikal/ficko-rodjen-da-bude-klasik-longsleeve-majica/",
    "Trekker Bike – Technical Breakdown":  "https://www.mashina.rs/artikal/trekker-bike-technical-breakdown-long-sleeve/",
    "WE LOVE DIRT":                        "https://www.mashina.rs/artikal/we-love-dirt-mashina-mtb-longsleeve-majica/",
    "Mashina Time Machines":               "https://www.mashina.rs/artikal/mashina-time-machines-retro-longsleeve-sa-siluetama-automobila/",
    "North. South. East. West. Shoot.":    "https://www.mashina.rs/artikal/north-south-east-west-shoot/",
    "Vinyl Culture":                       "https://www.mashina.rs/artikal/longsleeve-retro-majica-vinyl-culture/",
    "Hermann Hesse":                       "https://www.mashina.rs/artikal/longsleeve-majica-hermann-hesse/",
    "Che Guevara":                         "https://www.mashina.rs/artikal/longsleeve-retro-majica-che-guevara/",
    "Citroën Ajkula":                      "https://www.mashina.rs/artikal/longsleeve-retro-majica-citroen-ajkula/",
    "Bob Dylan":                           "https://www.mashina.rs/artikal/retro-longsleeve-majica-bob-dylan/",
    "Porsche 911 Blueprint":               "https://www.mashina.rs/artikal/porsche-911-blueprint-long-sleeve-retro-majica-sa-printom/",
  },
  Dukserica: {
    "I AM THE ENGINE":                     "https://www.mashina.rs/artikal/i-am-the-engine-duks-sa-ilustracijom-trekker-bicikliste/",
    "Trekker Bike – Technical Breakdown":  "https://www.mashina.rs/artikal/trekker-bike-technical-breakdown-mashina-duks/",
    "Unknown Pleasures":                   "https://www.mashina.rs/artikal/mashina-duks-sa-apstraktnim-waveform-signalom-i-natpisom-unknown-pleasures-minimalisticki-dizajn-inspirisan-zvukom-fokusom-i-licnim-ritmom/",
    "Peglica – Uspomena na točkovima":     "https://www.mashina.rs/artikal/peglica-uspomena-na-tockovima-duks/",
    "RAW IS REAL – Iggy Pop":              "https://www.mashina.rs/artikal/raw-is-real-iggy-pop-mashina-heritage-majica/",
    "TOMOS T12":                           "https://www.mashina.rs/artikal/tomos-t12-ne-staje-ne-pita-duks/",
    "ISKRA – Dvojnik na liniji":           "https://www.mashina.rs/artikal/iskra-dvojnik-na-liniji-mashina-retro-duks/",
    "Fićko – Rođen da bude klasik":        "https://www.mashina.rs/artikal/ficko-rodjen-da-bude-klasik-duks/",
    "WE LOVE DIRT":                        "https://www.mashina.rs/artikal/we-love-dirt-mtb-duks/",
    "Tour de Legend":                      "https://www.mashina.rs/artikal/tour-de-legend-mashina-trekker-duks/",
    "Mashina Time Machines":               "https://www.mashina.rs/artikal/time-machines-retro-duks/",
    "John Lennon – Dream Louder":          "https://www.mashina.rs/artikal/john-lennon-dream-louder-mashina-heritage-duks/",
  },
};

const DESIGN_LINKS_UPSTORE = {
  Majica: {
    "DIE YOUNG AS LATE AS POSSIBLE":  "https://upstore.rs/proizvod/die-young-as-late-as-possible/",
    "TECHNO UNDERGROUND":             "https://upstore.rs/proizvod/techno-underground/",
    "LESS DRAMA MORE TECHNO":         "https://upstore.rs/proizvod/less-drama-more-techno-majica/",
    "ACID TECHNO":                    "https://upstore.rs/proizvod/techno-majica-acid-techno/",
    "Minimal Techno":                 "https://upstore.rs/proizvod/minimal-techno-retro-majica-za-ljubitelje-elektronske-muzike/",
    "Nature Is Enough":               "https://upstore.rs/proizvod/nature-is-enough-majica/",
    "Expand Your Inner Space":        "https://upstore.rs/proizvod/retro-majica-expand-your-inner-space-psihodelicne-pecurke/",
    "Let's Dive In":                  "https://upstore.rs/proizvod/lets-dive-in-surf-majica/",
    "Reading Is Essential":           "https://upstore.rs/proizvod/reading-is-essential-majica/",
    "Out of Office":                  "https://upstore.rs/proizvod/out-of-office-majica-surf-wave-edition/",
    "Timeless Summer":                "https://upstore.rs/proizvod/majica-sa-printom-timeless-summer/",
    "More je peto godišnje doba":     "https://upstore.rs/proizvod/majica-sa-printom-more-je-peto-godisnje-doba/",
    "I JUST LOVE MYSELF":             "https://upstore.rs/proizvod/majica-sa-printom-i-just-love-myself/",
    "MOZAK U TEGLI":                  "https://upstore.rs/proizvod/majica-sa-printom-mozak-u-tegli/",
    "LOW BATTERY STILL WORKING":      "https://upstore.rs/proizvod/majica-sa-printom-robota/",
    "MARS, BUT MAKE IT CHILL":        "https://upstore.rs/proizvod/majica-sa-printom-mars-but-make-it-chill/",
    "I LOVE CATS":                    "https://upstore.rs/proizvod/majica-sa-printom-i-love-cats/",
    "Let's Go Places":                "https://upstore.rs/proizvod/majica-sa-printom-astronauta-lets-go-places/",
    "ZARONI":                         "https://upstore.rs/proizvod/majica-sa-printom-rethink-pogled-ispod-povrsine/",
    "Minimal Plivač":                 "https://upstore.rs/proizvod/majica-sa-printom-bazena-minimal-plivac/",
    "Samo Lagano":                    "https://upstore.rs/proizvod/majica-sa-printom-plaze-u-flasi-samo-lagano/",
    "I Do What I Want":               "https://upstore.rs/proizvod/majica-sa-printom-macke-i-do-what-i-want/",
    "Šta sad?":                       "https://upstore.rs/proizvod/majica-sa-printom-astronauta-sta-sad/",
    "Universo Paralello":             "https://upstore.rs/proizvod/majica-sa-printom-svemira-universo-paralello/",
    "Vanlife Sunset":                 "https://upstore.rs/proizvod/majica-sa-printom-kombija-vanlife-sunset/",
    "Stay Free":                      "https://upstore.rs/proizvod/stay-free-vintage-surf-tshirt-retro-beach/",
    "Spasilac na plaži":              "https://upstore.rs/proizvod/majica-sa-printom-spasilac-na-plazi/",
    "Horizont slobode":               "https://upstore.rs/proizvod/majica-sa-printom-mora-horizont/",
    "Pusti me na mo(i)ru":            "https://upstore.rs/proizvod/majica-sa-printom-mora-pusti-me-na-moiru/",
    "Hop-La!":                        "https://upstore.rs/proizvod/majica-sa-printom-lisice-hop-la/",
    "Made in Serbia":                 "https://upstore.rs/proizvod/majica-sa-printom-francuskog-buldoga-made-in-serbia/",
    "Glide Through Life":             "https://upstore.rs/proizvod/majica-sa-printom-rolsua-glide-through-life/",
    "Rollin' Honey":                  "https://upstore.rs/proizvod/majica-sa-printom-rolera-rollin-honey/",
    "Defy Gravity":                   "https://upstore.rs/proizvod/majica-sa-printom-bicikliste-defy-gravity/",
    "Live to Ride":                   "https://upstore.rs/proizvod/live-to-ride-majica-sa-printom-bicikla/",
    "Bike Hard":                      "https://upstore.rs/proizvod/majica-sa-printom-bicikliste-bike-hard/",
    "Wheelie Trick":                  "https://upstore.rs/proizvod/majica-road-bike-wheelie/",
    "Wheelie Trick – Colour":         "https://upstore.rs/proizvod/wheelie-trick-colour/",
  },
};

const DESIGN_LINKS = process.env.REACT_APP_BRAND === "upstore" ? DESIGN_LINKS_UPSTORE : DESIGN_LINKS_MASHINA;

function resolveLineImageUrl(line, allOrders) {
  if (line.image) return line.image;
  for (const order of allOrders) {
    for (const l of order.lines) {
      if (l.product === line.product && l.dizajn === line.dizajn && l.image) return l.image;
    }
  }
  return "";
}

function resolveDesignPermalink(product, dizajn) {
  if (!dizajn) return "";
  const catalog = DESIGN_LINKS[product] || {};
  if (catalog[dizajn]) return catalog[dizajn];
  const lower = dizajn.toLowerCase();
  const match = Object.entries(catalog).find(([k]) =>
    k.toLowerCase().includes(lower) || lower.includes(k.toLowerCase())
  );
  return match ? match[1] : "";
}

const COLOR_URL_SLUGS = {
  "Bela": "bela", "Crna": "crna", "Natural": "natural", "Khaki": "khaki",
  "Svetlo Grafit": "svetlo-grafit", "Maslinasto zelena": "maslinasto-zelena",
  "Svetlo plava": "svetlo-plava", "Pepeljasto teget": "pepeljasto-teget",
  "Svetlo zelena": "svetlo-zelena", "Melirana crvena": "melirana-crvena",
  "Burgundi": "burgundi",
};

// Purchase prices (nabavne cene) — each color is an array of { from, price } entries.
// getPurchasePrice picks the last entry where from <= orderDate.
const PURCHASE_PRICES = {
  Majica:            { Bela: [{ from: "2000-01-01", price: 495 }], Natural: [{ from: "2026-03-01", price: 778 }], _default: [{ from: "2000-01-01", price: 595 }] },
  Dukserica:         { Bela: [{ from: "2000-01-01", price: 1590 }], _default: [{ from: "2000-01-01", price: 1780 }] },
  "Longsleeve":      { Bela: [{ from: "2000-01-01", price: 790 }], _default: [{ from: "2000-01-01", price: 960 }] },
  Ceger:             { _default: [{ from: "2000-01-01", price: 100 }] },
  "Poklon pakovanje":{ _default: [{ from: "2000-01-01", price: 64 }] },
};

// Default selling prices (prodajne cene)
const DEFAULT_SELLING_PRICES = {
  Majica: 2480,
  Dukserica: 3700,
  "Longsleeve": 2820,
  Ceger: 850,
  "Poklon pakovanje": 450,
};

function getPurchasePrice(product, color, prices, orderDate) {
  const p = (prices || PURCHASE_PRICES)[product];
  if (!p) return 0;
  function resolve(entries) {
    if (entries == null) return null;
    if (!Array.isArray(entries)) return typeof entries === "number" ? entries : null;
    const applicable = entries.filter(e => !orderDate || e.from <= orderDate);
    return applicable.length > 0 ? applicable[applicable.length - 1].price : null;
  }
  const byColor = resolve(p[color]);
  if (byColor != null) return byColor;
  return resolve(p._default) ?? 0;
}

function getDefaultSellingPrice(product) {
  return DEFAULT_SELLING_PRICES[product] || 0;
}

const DEFAULT_PROD_COSTS = {
  Slika: 92, Etiketa: 12, Sivenje: 50,
  "Karton i driker": 10, Tag: 20, Nalepnica: 10, Pakovanje: 27,
};

// ── Initial Data ──────────────────────────────────────────────────────────────

const DEFAULT_PODELA_CONFIG = {
  participants: [
    { id: 1, name: "Bokima",  salary: 0, royaltyPerItem: 150, profitSharePct: 0,   locked: false },
    { id: 2, name: "Yumornar",salary: 0, royaltyPerItem: 0,   profitSharePct: 100, locked: true  },
    { id: 3, name: "Joca",    salary: 0, royaltyPerItem: 0,   profitSharePct: 0,   locked: false },
  ],
};

const initialContacts = [];

const initialOrders = [];

const initialExpenses = [];

const initialMarketingChannels = ["Meta","TikTok"];
const initialMarketingEntries = [];

function buildPrintStatuses(orders) {
  const s = {};
  orders.forEach(o => {
    if (!o.lines) return;
    const status = o.status === "spakovano" ? "spakovano" : (o.status === "odštampano" || o.status === "completed") ? "odštampano" : "pending";
    o.lines.forEach((l, li) => {
      for (let i = 0; i < l.qty; i++) s[`${o.id}-${li}-${i}`] = status;
    });
  });
  return s;
}

// ── UI Primitives ─────────────────────────────────────────────────────────────

function Badge({ type }) {
  const cfg = {
    web:           { bg: "#E1F5EE", color: "#0F6E56", label: "WooCommerce" },
    direct:        { bg: "#EEEDFE", color: "#534AB7", label: "Direktna" },
    print:         { bg: "#FBEAF0", color: "#993556", label: "Print servis" },
    completed:     { bg: "#EAF3DE", color: "#3B6D11", label: "Završeno" },
    odštampano:    { bg: "#EAF3DE", color: "#3B6D11", label: "Odštampano" },
    spakovano:     { bg: "#D6EEF5", color: "#185F7A", label: "Spakovano" },
    poslato:       { bg: "#E8E8E8", color: "#444", label: "Poslato" },
    "in progress": { bg: "#FAEEDA", color: "#854F0B", label: "U obradi" },
    cancelled:     { bg: "#FCEBEB", color: "#A32D2D", label: "Otkazano" },
  };
  const s = cfg[type];
  if (!s) return null;
  return <span style={{ background: s.bg, color: s.color, padding: "3px 8px", borderRadius: 20, fontSize: 11, fontWeight: 500, whiteSpace: "nowrap" }}>{s.label}</span>;
}

function MetricCard({ label, value, color, sub }) {
  return (
    <div style={{ background: "#f5f5f5", borderRadius: 8, padding: "12px 14px", color: "#111" }}>
      <div style={{ fontSize: 11, color: "#888", marginBottom: 5 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 500, color: color || "#111" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#aaa", marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

function Btn({ onClick, children, variant = "default", size = "md", style: extra }) {
  const base = { cursor: "pointer", borderRadius: 8, border: brand.btnBorder, background: "transparent", fontFamily: "inherit" };
  const variants = {
    default: { color: "inherit" },
    primary: { background: "#E24B4A", color: "#fff", border: "none" },
    danger:  { color: brand.dangerColor, border: `0.5px solid ${brand.dangerColor}` },
    success: { background: "#1D9E75", color: "#fff", border: "none" },
  };
  const sizes = { md: { padding: "7px 14px", fontSize: 13 }, sm: { padding: "4px 10px", fontSize: 12 }, xs: { padding: "2px 7px", fontSize: 11 } };
  return <button onClick={onClick} style={{ ...base, ...variants[variant], ...sizes[size], ...extra }}>{children}</button>;
}

function FormGroup({ label, children }) {
  return <div style={{ display: "flex", flexDirection: "column", gap: 4 }}><label style={{ fontSize: 12, color: "#888" }}>{label}</label>{children}</div>;
}

function Input(props) {
  return <input {...props} style={{ fontSize: 13, padding: "6px 8px", border: brand.inputBorder, borderRadius: 6, fontFamily: "inherit", width: "100%", boxSizing: "border-box", background: brand.inputBg, color: "inherit", ...(props.style || {}) }} />;
}

function Select({ children, ...props }) {
  return <select {...props} style={{ fontSize: 13, padding: "6px 8px", border: brand.inputBorder, borderRadius: 6, fontFamily: "inherit", width: "100%", boxSizing: "border-box", background: brand.inputBg, color: "inherit" }}>{children}</select>;
}

function PreviewBox({ rows }) {
  return (
    <div style={{ background: "#f5f5f5", borderRadius: 8, padding: 12, marginTop: 10, color: "#111" }}>
      {rows.filter(Boolean).map((r, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, fontWeight: r.bold ? 500 : 400, borderTop: r.bold ? "0.5px solid #ddd" : "none", paddingTop: r.bold ? 8 : 0, marginTop: r.bold ? 4 : 0, fontSize: 13 }}>
          <span style={{ color: r.muted ? "#888" : "#111" }}>{r.label}</span>
          <span style={{ color: r.color || "#111" }}>{r.value}</span>
        </div>
      ))}
    </div>
  );
}

function Modal({ open, onClose, title, children, footer }) {
  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 100, display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: 40, overflowY: "auto" }}>
      <div style={{ background: "#fff", borderRadius: 12, border: "0.5px solid #ddd", padding: 22, width: 520, maxWidth: "96%", maxHeight: "85vh", overflowY: "auto", color: "#111" }}>
        <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 18 }}>{title}</div>
        {children}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 18, paddingTop: 14, borderTop: "0.5px solid #eee" }}>{footer}</div>
      </div>
    </div>
  );
}

function SectionCard({ title, children }) {
  return (
    <div style={{ background: "#fff", border: "0.5px solid #eee", borderRadius: 12, padding: 14, marginBottom: 14, color: "#111" }}>
      {title && <div style={{ fontSize: 11, fontWeight: 500, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 12 }}>{title}</div>}
      {children}
    </div>
  );
}

function ScrollTable({ heads, children, emptyMsg }) {
  return (
    <div style={{ overflowX: "auto", borderRadius: 12, border: brand.cardBorder, background: brand.cardBg, color: brand.tableTextColor }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 800 }}>
        <thead>
          <tr>{heads.map(h => <th key={h} style={{ textAlign: "left", padding: "8px 10px", fontSize: 11, color: brand.tableHeaderColor, borderBottom: brand.cardBorder, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 500, whiteSpace: "nowrap", background: brand.cardBg }}>{h}</th>)}</tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
      {emptyMsg && <div style={{ padding: 20, textAlign: "center", color: "#aaa", fontSize: 13 }}>{emptyMsg}</div>}
    </div>
  );
}

function TD({ children, style: s }) {
  return <td style={{ padding: "9px 10px", borderBottom: "0.5px solid #eee", verticalAlign: "middle", whiteSpace: "nowrap", ...s }}>{children}</td>;
}

// ── Graph helpers ─────────────────────────────────────────────────────────────

const GRAPH_STYLES = ["horizontal","vertical","line","pie"];
const GRAPH_LABELS = { horizontal: "Horizontalni", vertical: "Vertikalni", line: "Linija", pie: "Pie" };

function GraphStylePicker({ value, onChange, styles }) {
  const ss = styles || GRAPH_STYLES;
  return (
    <div style={{ display: "flex", gap: 4 }}>
      {ss.map(s => (
        <button key={s} onClick={() => onChange(s)} style={{ padding: "3px 10px", fontSize: 11, borderRadius: 6, border: "0.5px solid #ccc", background: value === s ? "#E24B4A" : "transparent", color: value === s ? "#fff" : "#888", cursor: "pointer", fontFamily: "inherit" }}>{GRAPH_LABELS[s]}</button>
      ))}
    </div>
  );
}

function PieChart({ data, colorFn }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  if (!total) return <div style={{ color: "#aaa", fontSize: 13 }}>Nema podataka</div>;
  let cum = -Math.PI / 2;
  const cx = 90, cy = 90, r = 75;
  const slices = data.map(d => {
    const a = (d.value / total) * 2 * Math.PI;
    const x1 = cx + r * Math.cos(cum), y1 = cy + r * Math.sin(cum);
    cum += a;
    const x2 = cx + r * Math.cos(cum), y2 = cy + r * Math.sin(cum);
    return { ...d, path: `M${cx},${cy} L${x1.toFixed(1)},${y1.toFixed(1)} A${r},${r} 0 ${a > Math.PI ? 1 : 0},1 ${x2.toFixed(1)},${y2.toFixed(1)} Z` };
  });
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
      <svg width={180} height={180}>{slices.map(s => <path key={s.label} d={s.path} fill={colorFn(s.label)} stroke="#fff" strokeWidth={2} />)}</svg>
      <div>{data.map(d => <div key={d.label} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, fontSize: 13 }}><span style={{ width: 12, height: 12, borderRadius: 2, background: colorFn(d.label), display: "inline-block" }} /><span style={{ color: "#555" }}>{d.label}</span><span style={{ fontWeight: 500 }}>{d.value.toLocaleString()} RSD</span></div>)}</div>
    </div>
  );
}

function BarChart({ data, colorFn, vertical }) {
  const maxV = Math.max(...data.map(d => d.value), 1);
  if (vertical) {
    const bw = 36, gap = 16, h = 160;
    return (
      <div style={{ overflowX: "auto" }}>
        <svg width={data.length * (bw + gap) + 20} height={h + 40}>
          {data.map((d, i) => { const bh = Math.round(d.value / maxV * h), x = 10 + i * (bw + gap), y = h - bh; return <g key={d.label}><rect x={x} y={y} width={bw} height={bh} fill={colorFn(d.label)} rx={3} /><text x={x + bw / 2} y={y - 3} textAnchor="middle" fontSize={9} fill="#888">{d.value.toLocaleString()}</text><text x={x + bw / 2} y={h + 16} textAnchor="middle" fontSize={10} fill="#888">{d.label.length > 8 ? d.label.slice(0, 8) + "…" : d.label}</text></g>; })}
        </svg>
      </div>
    );
  }
  return <div>{data.map(d => <div key={d.label} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}><div style={{ fontSize: 12, color: "#888", width: 120, textAlign: "right", flexShrink: 0 }}>{d.label}</div><div style={{ flex: 1, background: "#f0f0f0", borderRadius: 4, height: 18, overflow: "hidden" }}><div style={{ width: Math.round(d.value / maxV * 100) + "%", height: "100%", background: colorFn(d.label), paddingLeft: 8, fontSize: 11, color: "#333", display: "flex", alignItems: "center" }}>{d.value.toLocaleString()}</div></div></div>)}</div>;
}

function FlexChart({ data, colorFn, chartStyle }) {
  if (!data.length) return <div style={{ color: "#aaa", fontSize: 13 }}>Nema podataka</div>;
  if (chartStyle === "pie") return <PieChart data={data} colorFn={colorFn} />;
  return <BarChart data={data} colorFn={colorFn} vertical={chartStyle === "vertical"} />;
}

function MultiBarChart({ data, keys, pieKeys, colors, maxVal, chartStyle, height }) {
  if (!data.length) return <div style={{ color: "#aaa", fontSize: 13 }}>Nema podataka</div>;
  const pk = pieKeys || keys;
  if (chartStyle === "line") return <LineChart data={data} keys={keys} colors={colors} height={height || 170} />;
  if (chartStyle === "pie") return <PieChart data={pk.map(k => ({ label: k, value: data.reduce((s, d) => s + (d[k] || 0), 0) }))} colorFn={l => colors[l]} />;
  if (chartStyle === "vertical") {
    const bw = 18, gap = 6, gg = 28, h = height || 160, gw = keys.length * (bw + gap) - gap;
    return (
      <div style={{ overflowX: "auto" }}>
        <svg width={data.length * (gw + gg) + 20} height={h + 40}>
          {data.map((d, gi) => { const gx = 10 + gi * (gw + gg); return <g key={d.label}>{keys.map((k, ki) => { const bh = Math.max(0, Math.round((d[k] || 0) / maxVal * h)), x = gx + ki * (bw + gap), y = h - bh; return <g key={k}><rect x={x} y={y} width={bw} height={bh} fill={colors[k]} rx={3} /><text x={x + bw / 2} y={y - 3} textAnchor="middle" fontSize={9} fill="#888">{(d[k] || 0).toLocaleString()}</text></g>; })}<text x={gx + gw / 2} y={h + 16} textAnchor="middle" fontSize={11} fill="#888">{d.label}</text></g>; })}
        </svg>
      </div>
    );
  }
  return <div>{data.map(d => <div key={d.label} style={{ marginBottom: 12 }}><div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>{d.label}</div>{keys.map(k => <div key={k} style={{ display: "flex", marginBottom: 3 }}><div style={{ flex: 1, background: "#f0f0f0", borderRadius: 4, height: 18, overflow: "hidden" }}><div style={{ width: Math.round((d[k] || 0) / maxVal * 100) + "%", height: "100%", background: colors[k], paddingLeft: 8, fontSize: 11, color: "#333", display: "flex", alignItems: "center" }}>{(d[k] || 0).toLocaleString()}</div></div></div>)}</div>)}</div>;
}

function LineChart({ data, keys, colors, height = 170 }) {
  if (!data.length) return <div style={{ color: "#aaa", fontSize: 13 }}>Nema podataka</div>;
  const W = Math.max(560, data.length * 42);
  const H = height, padL = 60, padB = 28, padT = 12, padR = 12;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;
  const allVals = data.flatMap(d => keys.map(k => d[k] || 0));
  const rawMax = Math.max(...allVals, 0);
  const rawMin = Math.min(...allVals, 0);
  const vPad = (rawMax - rawMin) * 0.12 || 1000;
  const maxV = rawMax + vPad;
  const minV = Math.min(rawMin - vPad, 0);
  const range = maxV - minV || 1;
  const xOf = i => padL + (data.length > 1 ? (i / (data.length - 1)) * chartW : chartW / 2);
  const yOf = v => padT + chartH * (1 - (v - minV) / range);
  const YTICKS = 5;
  const tickStep = range / YTICKS;
  return (
    <div style={{ overflowX: "auto" }}>
      <svg width={W} height={H} style={{ display: "block" }}>
        {Array.from({ length: YTICKS + 1 }, (_, i) => {
          const v = minV + i * tickStep;
          const y = yOf(v);
          return <g key={i}><line x1={padL} x2={W - padR} y1={y} y2={y} stroke="#f0f0f0" strokeWidth={1} /><text x={padL - 4} y={y + 4} textAnchor="end" fontSize={9} fill="#bbb">{Math.round(v).toLocaleString()}</text></g>;
        })}
        {rawMin < 0 && <line x1={padL} x2={W - padR} y1={yOf(0)} y2={yOf(0)} stroke="#e0e0e0" strokeWidth={1.5} strokeDasharray="4,3" />}
        {keys.map(k => {
          const pts = data.map((d, i) => [xOf(i), yOf(d[k] || 0)]);
          const path = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
          return <g key={k}><path d={path} fill="none" stroke={colors[k]} strokeWidth={2} strokeLinejoin="round" />{pts.map(([x, y], i) => <circle key={i} cx={x} cy={y} r={3} fill={colors[k]} />)}</g>;
        })}
        {data.map((d, i) => <text key={i} x={xOf(i)} y={H - 6} textAnchor="middle" fontSize={9} fill="#888">{d.label}</text>)}
      </svg>
    </div>
  );
}

const EXP_PALETTE = ["#5DCAA5","#85B7EB","#AFA9EC","#F0997B","#B4B2A9","#FAC775","#ED93B1"];
const expColorMap = {};
let expColorIdx = 0;
function expColorFn(label) { if (!expColorMap[label]) expColorMap[label] = EXP_PALETTE[expColorIdx++ % EXP_PALETTE.length]; return expColorMap[label]; }
const MKT_COLORS = { Meta: "#F0997B", TikTok: "#AFA9EC", Google: "#85B7EB", Facebook: "#5DCAA5" };
function mktColorFn(l) { return MKT_COLORS[l] || expColorFn(l); }
const REV_COLORS = { Prihod: "#5DCAA5", "PDV": "#9B8FE0", Troškovi: "#F0997B", Marketing: "#F7C59F", "Proiz. troškovi": "#BDBDBD", Dostava: "#F4A261", "Neto Dobit": "#85B7EB" };
const TREND_KEYS = ["Prihod", "Neto + PDV", "Troškovi", "Marketing", "Proiz. troškovi"];
const TREND_COLORS = { "Prihod": "#1D9E75", "Neto + PDV": "#534AB7", "Troškovi": "#E24B4A", "Marketing": "#F4A261", "Proiz. troškovi": "#BDBDBD" };

// ── Dashboard ─────────────────────────────────────────────────────────────────

function Dashboard({ orders, expenses, marketingEntries, prodCosts, purchasePrices, fromDate, toDate, setFromDate, setToDate, shippingCfg, noShippingIds }) {
  const [revStyle, setRevStyle] = useState(() => localStorage.getItem("revChart") || "horizontal");
  const [expStyle, setExpStyle] = useState(() => localStorage.getItem("expChart") || "horizontal");
  const [mktStyle, setMktStyle] = useState(() => localStorage.getItem("mktChart") || "horizontal");
  const [trendStyle, setTrendStyle] = useState(() => localStorage.getItem("trendChart") || "vertical");
  const [trendVisible, setTrendVisible] = useState(() => {
    try { return JSON.parse(localStorage.getItem("trendVisible")) || {}; } catch { return {}; }
  });
  function isTrendVisible(k) { return trendVisible[k] !== false; }
  function toggleTrend(k) {
    const next = { ...trendVisible, [k]: !isTrendVisible(k) };
    setTrendVisible(next);
    localStorage.setItem("trendVisible", JSON.stringify(next));
  }
  const [statsView, setStatsView] = useState("dizajni");
  function setRS(s) { setRevStyle(s); localStorage.setItem("revChart", s); }
  function setES(s) { setExpStyle(s); localStorage.setItem("expChart", s); }
  function setMS(s) { setMktStyle(s); localStorage.setItem("mktChart", s); }
  function setTS(s) { setTrendStyle(s); localStorage.setItem("trendChart", s); }

  const fOrders = orders.filter(o => o.date >= fromDate && o.date <= toDate && o.status !== "cancelled");
  const fExp = expenses.filter(e => e.date >= fromDate && e.date <= toDate);
  const fMkt = marketingEntries.filter(e => e.date >= fromDate && e.date <= toDate);
  const revenue = fOrders.reduce((s, o) => s + o.total, 0);
  const totalExp = fExp.reduce((s, e) => s + e.amount, 0);
  const totalMkt = fMkt.reduce((s, e) => s + e.amount, 0);
  const profitData = fOrders.map(o => calcOrderProfit(o, prodCosts, purchasePrices, marketingEntries, orders, { ...shippingCfg, noShipping: noShippingIds?.has(String(o.id)) }));
  const daysWithWebOrders = new Set(fOrders.filter(o => o.type === "web").map(o => o.date));
  const unallocatedMkt = fMkt.reduce((s, e) => daysWithWebOrders.has(e.date) ? s : s + e.amount, 0);
  const totalShipping = profitData.reduce((s, p) => s + p.shipping, 0);
  const totalNeto = profitData.reduce((s, p) => s + p.netoDobit, 0) - unallocatedMkt - totalExp;
  const totalPdv = profitData.reduce((s, p) => s + p.pdv, 0);
  const totalProdCosts = fOrders.reduce((s, o) => s + o.lines.reduce((ls, l) => ls + (totalProdCost(getPurchasePrice(l.product, l.color, purchasePrices, o.date), getApplicableCosts(l.product, prodCosts)) - getPurchasePrice(l.product, l.color, purchasePrices, o.date)) * l.qty, 0), 0);
  const totalPurchase = fOrders.reduce((s, o) => s + o.lines.reduce((ls, l) => ls + getPurchasePrice(l.product, l.color, purchasePrices, o.date) * l.qty, 0), 0);
  const totalUPC = totalPurchase + totalProdCosts;
  const avgPct = profitData.length > 0 ? profitData.reduce((s, p) => s + p.pctZarade, 0) / profitData.length : 0;

  const productMargins = {};
  fOrders.forEach(o => {
    if (o.type === "print" || !o.lines.length) return;
    o.lines.forEach(l => {
      const pp = getPurchasePrice(l.product, l.color, purchasePrices, o.date);
      const applicableCosts = SIMPLE_PRODUCTS.includes(l.product) ? {} : PRODUCT_COST_KEYS[l.product] ? Object.fromEntries(Object.entries(prodCosts).filter(([k]) => PRODUCT_COST_KEYS[l.product].includes(k))) : prodCosts;
      const r = calcItemProfit(l.price, pp, applicableCosts);
      if (!productMargins[l.product]) productMargins[l.product] = { total: 0, count: 0 };
      productMargins[l.product].total += l.price > 0 ? r.netoDobit / l.price * 100 : 0;
      productMargins[l.product].count += l.qty;
    });
  });
  const marginEntries = Object.entries(productMargins).map(([k, v]) => ({ product: k, avg: v.total / v.count })).sort((a, b) => b.avg - a.avg);
  const bestProduct = marginEntries[0];
  const worstProduct = marginEntries[marginEntries.length - 1];

  const weekData = {};
  fOrders.forEach(o => {
    const w = `W${Math.ceil(new Date(o.date).getDate() / 7)}`;
    if (!weekData[w]) weekData[w] = { label: w, Prihod: 0, PDV: 0, Troškovi: 0, Marketing: 0, "Proiz. troškovi": 0, Dostava: 0, "Neto Dobit": 0 };
    const p = calcOrderProfit(o, prodCosts, purchasePrices, marketingEntries, orders, { ...shippingCfg, noShipping: noShippingIds?.has(String(o.id)) });
    weekData[w].Prihod += o.total;
    weekData[w].PDV += p.pdv;
    weekData[w].Dostava += p.shipping;
    weekData[w]["Neto Dobit"] += p.netoDobit;
    weekData[w]["Proiz. troškovi"] += o.lines.reduce((s, l) => s + totalProdCost(getPurchasePrice(l.product, l.color, purchasePrices, o.date), getApplicableCosts(l.product, prodCosts)) * l.qty, 0);
  });
  fExp.forEach(e => {
    const w = `W${Math.ceil(new Date(e.date).getDate() / 7)}`;
    if (!weekData[w]) weekData[w] = { label: w, Prihod: 0, PDV: 0, Troškovi: 0, Marketing: 0, "Proiz. troškovi": 0, Dostava: 0, "Neto Dobit": 0 };
    weekData[w].Troškovi += e.amount;
  });
  fMkt.forEach(e => {
    const w = `W${Math.ceil(new Date(e.date).getDate() / 7)}`;
    if (!weekData[w]) weekData[w] = { label: w, Prihod: 0, PDV: 0, Troškovi: 0, Marketing: 0, "Proiz. troškovi": 0, Dostava: 0, "Neto Dobit": 0 };
    weekData[w].Marketing += e.amount;
    if (!daysWithWebOrders.has(e.date)) {
      weekData[w]["Neto Dobit"] -= e.amount;
    }
  });
  const weeks = Object.values(weekData).sort((a, b) => a.label.localeCompare(b.label));
  const maxW = Math.max(...weeks.flatMap(w => [w.Prihod, w.PDV, w.Troškovi, w.Marketing, w["Proiz. troškovi"], w.Dostava, w["Neto Dobit"]]), 1);

  // trend chart: Prihod and Neto+PDV bucketed by day/week/month
  const daysDiff = Math.max(1, (new Date(toDate) - new Date(fromDate)) / 86400000 + 1);
  const trendBucket = daysDiff <= 60 ? "day" : daysDiff <= 180 ? "week" : "month";
  function trendKey(dateStr) {
    if (trendBucket === "day") return dateStr;
    if (trendBucket === "month") return dateStr.slice(0, 7);
    const startMs = new Date(fromDate).getTime();
    const diff = Math.floor((new Date(dateStr).getTime() - startMs) / (7 * 86400000));
    const ws = new Date(startMs + diff * 7 * 86400000);
    return ws.toISOString().slice(0, 10);
  }
  function trendLabel(key) {
    const p = key.split("-");
    if (trendBucket === "month") return `${p[1]}.${p[0].slice(2)}`;
    return `${p[2]}.${p[1]}`;
  }
  const trendBuckets = {};
  const ensureTB = k => { if (!trendBuckets[k]) trendBuckets[k] = { key: k, Prihod: 0, "Neto + PDV": 0, "Troškovi": 0, "Marketing": 0, "Proiz. troškovi": 0 }; };
  fOrders.forEach(o => {
    const k = trendKey(o.date);
    ensureTB(k);
    const p = calcOrderProfit(o, prodCosts, purchasePrices, marketingEntries, orders, { ...shippingCfg, noShipping: noShippingIds?.has(String(o.id)) });
    trendBuckets[k].Prihod += o.total;
    trendBuckets[k]["Neto + PDV"] += p.netoDobit + p.pdv;
    trendBuckets[k]["Proiz. troškovi"] += o.lines.reduce((s, l) => s + totalProdCost(getPurchasePrice(l.product, l.color, purchasePrices, o.date), getApplicableCosts(l.product, prodCosts)) * l.qty, 0);
  });
  fExp.forEach(e => {
    const k = trendKey(e.date);
    ensureTB(k);
    trendBuckets[k]["Troškovi"] += e.amount;
  });
  fMkt.forEach(e => {
    const k = trendKey(e.date);
    ensureTB(k);
    trendBuckets[k]["Marketing"] += e.amount;
    if (!daysWithWebOrders.has(e.date)) trendBuckets[k]["Neto + PDV"] -= e.amount;
  });
  const trendData = Object.values(trendBuckets).sort((a, b) => a.key.localeCompare(b.key)).map(d => ({ ...d, label: trendLabel(d.key) }));

  const expCats = {};
  fExp.forEach(e => { expCats[e.category] = (expCats[e.category] || 0) + e.amount; });
  const mktCh = {};
  fMkt.forEach(e => { mktCh[e.channel] = (mktCh[e.channel] || 0) + e.amount; });

  const designCounts = {};
  fOrders.forEach(o => {
    if (o.type === "print") return;
    o.lines.forEach(l => {
      if (!l.dizajn) return;
      designCounts[l.dizajn] = (designCounts[l.dizajn] || 0) + l.qty;
    });
  });
  const totalDesignQty = Object.values(designCounts).reduce((s, v) => s + v, 0);
  const designData = Object.entries(designCounts)
    .map(([label, qty]) => ({ label, value: totalDesignQty > 0 ? parseFloat((qty / totalDesignQty * 100).toFixed(1)) : 0, qty }))
    .sort((a, b) => b.value - a.value);

  const variantData = {};
  fOrders.forEach(o => {
    if (o.type === "print") return;
    o.lines.forEach(l => {
      if (!l.product) return;
      if (!variantData[l.product]) variantData[l.product] = {};
      const variant = [l.color, l.size].filter(Boolean).join(" / ") || "Standard";
      variantData[l.product][variant] = (variantData[l.product][variant] || 0) + l.qty;
    });
  });
  const variantProducts = Object.entries(variantData)
    .map(([product, variants]) => ({
      product,
      total: Object.values(variants).reduce((s, v) => s + v, 0),
      variants: Object.entries(variants).sort((a, b) => b[1] - a[1]),
    }))
    .sort((a, b) => b.total - a.total);

  return (
    <div>
      <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 16 }}>Dashboard</div>
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <label style={{ fontSize: 12, color: "#888" }}>Od</label>
        <Input type="date" value={fromDate} onChange={e => { setFromDate(e.target.value); localStorage.setItem("dashFrom", e.target.value); }} style={{ width: 150 }} />
        <label style={{ fontSize: 12, color: "#888" }}>Do</label>
        <Input type="date" value={toDate} onChange={e => { setToDate(e.target.value); localStorage.setItem("dashTo", e.target.value); }} style={{ width: 150 }} />
        <Btn size="sm">Primeni</Btn>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: 10, marginBottom: 16 }}>
        <MetricCard label="Prihod" value={revenue.toLocaleString() + " RSD"} color="#1D9E75" />
        <MetricCard label="PDV (20%)" value={Math.round(totalPdv).toLocaleString() + " RSD"} color="#534AB7" />
        <MetricCard label="Troškovi" value={totalExp.toLocaleString() + " RSD"} color="#E24B4A" />
        <MetricCard label="Marketing" value={totalMkt.toLocaleString() + " RSD"} color="#854F0B" />
        <MetricCard label="Proiz. troškovi" value={Math.round(totalUPC).toLocaleString() + " RSD"} color="#888" />
        <MetricCard label="Dostava" value={totalShipping.toLocaleString() + " RSD"} color="#F4A261" />
        <MetricCard label="Neto Dobit" value={Math.round(totalNeto).toLocaleString() + " RSD"} color={totalNeto >= 0 ? "#1D9E75" : "#E24B4A"} />
        <MetricCard label="Avg % Zarade" value={avgPct.toFixed(1) + "%"} color={avgPct >= 0 ? "#1D9E75" : "#E24B4A"} />
        <MetricCard label="Neto Dobit + PDV" value={Math.round(totalNeto + totalPdv).toLocaleString() + " RSD"} color={(totalNeto + totalPdv) >= 0 ? "#1D9E75" : "#E24B4A"} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
        <SectionCard title="Najbolji proizvod">
          {bestProduct ? <><div style={{ fontSize: 17, fontWeight: 500, color: "#1D9E75" }}>{bestProduct.product}</div><div style={{ fontSize: 12, color: "#888" }}>{bestProduct.avg.toFixed(1)}% avg zarade</div></> : <div style={{ color: "#aaa", fontSize: 13 }}>Nema podataka</div>}
        </SectionCard>
        <SectionCard title="Najlošiji proizvod">
          {worstProduct && worstProduct.product !== bestProduct?.product ? <><div style={{ fontSize: 17, fontWeight: 500, color: "#E24B4A" }}>{worstProduct.product}</div><div style={{ fontSize: 12, color: "#888" }}>{worstProduct.avg.toFixed(1)}% avg zarade</div></> : <div style={{ color: "#aaa", fontSize: 13 }}>Nema podataka</div>}
        </SectionCard>
      </div>
      <SectionCard title={null}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {TREND_KEYS.map(k => (
              <button key={k} onClick={() => toggleTrend(k)} style={{ display: "flex", alignItems: "center", gap: 5, padding: "3px 8px", borderRadius: 6, border: `1.5px solid ${isTrendVisible(k) ? TREND_COLORS[k] : "#ddd"}`, background: isTrendVisible(k) ? TREND_COLORS[k] + "18" : "transparent", cursor: "pointer", fontFamily: "inherit", fontSize: 11, color: isTrendVisible(k) ? "#333" : "#bbb" }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: isTrendVisible(k) ? TREND_COLORS[k] : "#ddd", display: "inline-block", flexShrink: 0 }} />{k}
              </button>
            ))}
          </div>
          <GraphStylePicker value={trendStyle} onChange={setTS} styles={["vertical", "line"]} />
        </div>
        {(() => {
          const activeKeys = TREND_KEYS.filter(k => isTrendVisible(k));
          const maxVal = trendData.length && activeKeys.length ? Math.max(...trendData.flatMap(d => activeKeys.map(k => d[k] || 0)), 1) : 1;
          return <MultiBarChart data={trendData} keys={activeKeys} colors={TREND_COLORS} maxVal={maxVal} chartStyle={trendStyle} height={320} />;
        })()}
      </SectionCard>
      <SectionCard title={null}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 500, color: "#888", textTransform: "uppercase", letterSpacing: 0.5 }}>Prihod / PDV / Troškovi / Marketing / Proiz. troškovi / Dostava / Neto Dobit</div>
          <GraphStylePicker value={revStyle} onChange={setRS} />
        </div>
        {revStyle !== "pie" && <div style={{ display: "flex", gap: 16, marginBottom: 10, fontSize: 11, color: "#888" }}>{Object.entries(REV_COLORS).map(([k, c]) => <span key={k} style={{ display: "flex", alignItems: "center", gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: c, display: "inline-block" }} />{k}</span>)}</div>}
        <MultiBarChart data={weeks} keys={["Prihod","PDV","Troškovi","Marketing","Proiz. troškovi","Dostava","Neto Dobit"]} pieKeys={["PDV","Troškovi","Marketing","Proiz. troškovi","Dostava","Neto Dobit"]} colors={REV_COLORS} maxVal={maxW} chartStyle={revStyle} />
      </SectionCard>
      <SectionCard title={null}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 500, color: "#888", textTransform: "uppercase", letterSpacing: 0.5 }}>Troškovi po kategoriji</div>
          <GraphStylePicker value={expStyle} onChange={setES} />
        </div>
        <FlexChart data={Object.entries(expCats).map(([label, value]) => ({ label, value }))} colorFn={expColorFn} chartStyle={expStyle} />
      </SectionCard>
      <SectionCard title={null}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 500, color: "#888", textTransform: "uppercase", letterSpacing: 0.5 }}>Marketing po kanalu</div>
          <GraphStylePicker value={mktStyle} onChange={setMS} />
        </div>
        <FlexChart data={Object.entries(mktCh).map(([label, value]) => ({ label, value }))} colorFn={mktColorFn} chartStyle={mktStyle} />
      </SectionCard>
      <SectionCard title={null}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <div style={{ display: "flex", background: "#f5f5f5", borderRadius: 7, padding: 3, gap: 2 }}>
            {[["dizajni", "Popularnost dizajna"], ["varijante", "Boje i veličine"]].map(([key, label]) => (
              <button key={key} onClick={() => setStatsView(key)} style={{ padding: "4px 12px", borderRadius: 5, border: "none", background: statsView === key ? "#fff" : "transparent", fontWeight: statsView === key ? 500 : 400, cursor: "pointer", fontSize: 12, fontFamily: "inherit", boxShadow: statsView === key ? "0 0 0 0.5px #ddd" : "none", color: statsView === key ? "#333" : "#888" }}>{label}</button>
            ))}
          </div>
        </div>
        {statsView === "dizajni" && (<>
          {designData.length === 0 && <div style={{ color: "#aaa", fontSize: 13 }}>Nema podataka</div>}
          <div style={{ maxHeight: 310, overflowY: "auto" }}>
            {designData.map((d, i) => (
              <div key={d.label} style={{ marginBottom: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3 }}>
                  <span style={{ color: "#333", fontWeight: i < 3 ? 500 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "75%" }}>{d.label}</span>
                  <span style={{ color: "#888", flexShrink: 0 }}>{d.value}% <span style={{ color: "#bbb" }}>({d.qty} kom)</span></span>
                </div>
                <div style={{ height: 6, background: "#f0f0f0", borderRadius: 3, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${d.value}%`, background: i === 0 ? "#1D9E75" : i === 1 ? "#5DCAA5" : i === 2 ? "#85B7EB" : "#AFA9EC", borderRadius: 3, transition: "width 0.3s" }} />
                </div>
              </div>
            ))}
          </div>
        </>)}
        {statsView === "varijante" && (<>
          {variantProducts.length === 0 && <div style={{ color: "#aaa", fontSize: 13 }}>Nema podataka</div>}
          <div style={{ maxHeight: 380, overflowY: "auto" }}>
            {variantProducts.map(({ product, total, variants }) => (
              <div key={product} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#333", marginBottom: 6, display: "flex", justifyContent: "space-between" }}>
                  <span>{product}</span>
                  <span style={{ color: "#888", fontWeight: 400 }}>{total} kom</span>
                </div>
                {variants.map(([variant, qty], i) => (
                  <div key={variant} style={{ marginBottom: 5, paddingLeft: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 2 }}>
                      <span style={{ color: "#555" }}>{variant}</span>
                      <span style={{ color: "#888" }}>{qty} kom</span>
                    </div>
                    <div style={{ height: 5, background: "#f0f0f0", borderRadius: 3, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${Math.round(qty / total * 100)}%`, background: i === 0 ? "#85B7EB" : i === 1 ? "#AFA9EC" : "#BDBDBD", borderRadius: 3, transition: "width 0.3s" }} />
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </>)}
      </SectionCard>
    </div>
  );
}

function DesignAutocomplete({ value, onChange, suggestions }) {
  const [open, setOpen] = useState(false);
  const filtered = value.length > 0
    ? suggestions.filter(s => s.toLowerCase().includes(value.toLowerCase()))
    : suggestions;
  return (
    <div style={{ position: "relative" }}>
      <Input
        value={value}
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onFocus={() => setOpen(true)}
        placeholder="Npr. Stari Fića – Legenda na majici"
      />
      {open && filtered.length > 0 && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "0.5px solid #ccc", borderRadius: 6, zIndex: 20, maxHeight: 160, overflowY: "auto" }}>
          {filtered.map(s => (
            <div key={s}
              onMouseDown={() => { onChange(s); setOpen(false); }}
              style={{ padding: "8px 10px", cursor: "pointer", fontSize: 13 }}
              onMouseEnter={e => e.currentTarget.style.background = "#f5f5f5"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              {s}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Product Line Editor ───────────────────────────────────────────────────────

function ProductLineEditor({ lines, setLines, orders = [] }) {
  const designsByProduct = useMemo(() => {
    const map = {};
    orders.forEach(o => o.lines.forEach(l => {
      if (!l.dizajn || !l.product) return;
      if (!map[l.product]) map[l.product] = new Set();
      map[l.product].add(l.dizajn);
    }));
    return map;
  }, [orders]);
  const products = Object.keys(PRODUCT_CATALOG);

  function addLine() {
    const product = products[0];
    const noVariant = NO_VARIANTS.includes(product);
    setLines([...lines, { product, color: noVariant ? "" : PRODUCT_CATALOG[product][0], size: noVariant ? "" : "M", dizajn: "", qty: 1, price: getDefaultSellingPrice(product) }]);
  }

  function removeLine(i) { setLines(lines.filter((_, idx) => idx !== i)); }

  function updateLine(i, field, value) {
    setLines(lines.map((l, idx) => {
      if (idx !== i) return l;
      if (field === "product") {
        const noVariant = NO_VARIANTS.includes(value);
        const permalink = resolveDesignPermalink(value, l.dizajn);
        return { ...l, product: value, color: noVariant ? "" : PRODUCT_CATALOG[value][0], size: noVariant ? "" : l.size, price: getDefaultSellingPrice(value), permalink };
      }
      if (field === "dizajn") {
        const permalink = resolveDesignPermalink(l.product, value);
        return { ...l, dizajn: value, permalink };
      }
      return { ...l, [field]: value };
    }));
  }

  return (
    <div>
      <div style={{ fontSize: 12, color: "#888", marginBottom: 8 }}>Proizvodi</div>
      {lines.map((l, i) => (
        <div key={i} style={{ border: "0.5px solid #ddd", borderRadius: 8, padding: 12, marginBottom: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10, fontSize: 13, fontWeight: 500 }}>
            <span>Proizvod {i + 1}</span>
            <button onClick={() => removeLine(i)} style={{ background: "none", border: "none", color: "#E24B4A", cursor: "pointer", fontSize: 16 }}>×</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
            <FormGroup label="Proizvod">
              <Select value={l.product} onChange={e => updateLine(i, "product", e.target.value)}>
                {products.map(p => <option key={p}>{p}</option>)}
              </Select>
            </FormGroup>
            <FormGroup label="Količina">
              <Input type="number" min="1" value={l.qty} onChange={e => updateLine(i, "qty", parseInt(e.target.value) || 1)} />
            </FormGroup>
          </div>
          {!NO_VARIANTS.includes(l.product) && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
              <FormGroup label="Boja">
                <Select value={l.color} onChange={e => updateLine(i, "color", e.target.value)}>
                  {(PRODUCT_CATALOG[l.product] || []).filter(c => c !== "Bez boje").map(c => <option key={c}>{c}</option>)}
                </Select>
              </FormGroup>
              <FormGroup label="Veličina">
                <Select value={l.size} onChange={e => updateLine(i, "size", e.target.value)}>
                  {SIZES.map(s => <option key={s}>{s}</option>)}
                </Select>
              </FormGroup>
              <FormGroup label="Prod. cena (RSD)">
                <Input type="number" value={l.price} onChange={e => updateLine(i, "price", parseFloat(e.target.value) || 0)} />
              </FormGroup>
            </div>
          )}
          {NO_VARIANTS.includes(l.product) && (
            <div style={{ marginBottom: 10 }}>
              <FormGroup label="Prod. cena (RSD)">
                <Input type="number" value={l.price} onChange={e => updateLine(i, "price", parseFloat(e.target.value) || 0)} />
              </FormGroup>
            </div>
          )}
          <FormGroup label="Naziv dizajna / ilustracije">
            <DesignAutocomplete
              value={l.dizajn || ""}
              onChange={v => updateLine(i, "dizajn", v)}
              suggestions={[...new Set([
                ...Object.keys(DESIGN_LINKS[l.product] || {}),
                ...(designsByProduct[l.product] || []),
              ])].sort()}
            />
          </FormGroup>
        </div>
      ))}
      <Btn size="sm" onClick={addLine}>+ Dodaj proizvod</Btn>
    </div>
  );
}

// ── Direct Sale Modal ─────────────────────────────────────────────────────────

function DirectSaleModal({ open, onClose, onSave, contacts, setContacts, editOrder, orders = [] }) {
  const firstProduct = Object.keys(PRODUCT_CATALOG)[0];
  const blank = {
    customer: "", phone: "", email: "", address: "", city: "", date: today(), discount: 0, napomena: "",
    lines: [{ product: firstProduct, color: PRODUCT_CATALOG[firstProduct][0], size: "M", dizajn: "", qty: 1, price: getDefaultSellingPrice(firstProduct) }]
  };
  const [form, setForm] = useState(blank);
  const [showSugg, setShowSugg] = useState(false);
  const [showNewContact, setShowNewContact] = useState(false);
  const [newContact, setNewContact] = useState({ first: "", last: "", phone: "", email: "", address: "", note: "" });

  function saveNewContact() {
    if (!newContact.first && !newContact.last) return;
    const contact = { id: Date.now(), ...newContact };
    setContacts(prev => [...prev, contact]);
    setForm(f => ({ ...f, customer: [newContact.first, newContact.last].filter(Boolean).join(" "), phone: newContact.phone || "", email: newContact.email || "", address: newContact.address || "", city: "" }));
    setNewContact({ first: "", last: "", phone: "", email: "", address: "", note: "" });
    setShowNewContact(false);
  }

  useMemo(() => {
    if (!open) return;
    if (editOrder) {
      const c = contacts.find(x => x.id === editOrder.customerId);
      setForm({
        customer: editOrder.customer,
        phone: editOrder.phone || (c ? c.phone : ""),
        email: editOrder.email || (c ? c.email : ""),
        address: editOrder.address || (c ? c.address : ""),
        city: editOrder.city || "",
        date: editOrder.date,
        discount: editOrder.discount || 0,
        napomena: editOrder.napomena || "",
        lines: editOrder.lines.length ? editOrder.lines : blank.lines,
      });
    } else {
      setForm(blank);
    }
  // eslint-disable-next-line
  }, [open, editOrder]);

  const suggestions = form.customer.length > 0
    ? contacts.filter(c => (c.first + " " + c.last).toLowerCase().includes(form.customer.toLowerCase()))
    : [];

  const subtotal = form.lines.reduce((s, l) => s + l.price * l.qty, 0);
  const discountAmt = subtotal * (form.discount / 100);
  const total = subtotal - discountAmt;
  const pdv = pdvFromPrice(total);

  function handleSave() {
    const contact = contacts.find(c => c.first + " " + c.last === form.customer);
    onSave({
      customer: form.customer,
      customerId: contact ? contact.id : null,
      email: form.email,
      phone: form.phone,
      address: form.address,
      city: form.city || (form.address ? form.address.split(",").find(p => p.trim()) || "" : ""),
      date: form.date,
      discount: form.discount,
      napomena: form.napomena || "",
      lines: form.lines,
      total: parseFloat(total.toFixed(2)),
    });
    onClose();
  }

  return (
    <Modal open={open} onClose={onClose}
      title={editOrder ? "Izmeni direktnu prodaju" : "Nova direktna prodaja"}
      footer={<><Btn onClick={onClose}>Otkaži</Btn><Btn variant="primary" onClick={handleSave}>{editOrder ? "Sačuvaj" : "Kreiraj"}</Btn></>}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
        <FormGroup label={<span style={{ display: "flex", alignItems: "center", gap: 8 }}>Kupac <span onClick={() => setShowNewContact(true)} style={{ fontSize: 11, color: "#534AB7", cursor: "pointer", fontWeight: 500 }}>+ Novi kontakt</span></span>}>
          <div style={{ position: "relative" }}>
            <Input
              value={form.customer}
              onChange={e => { setForm({ ...form, customer: e.target.value }); setShowSugg(true); }}
              onBlur={() => setTimeout(() => setShowSugg(false), 150)}
              placeholder="Pretraži ili unesi…"
            />
            {showSugg && suggestions.length > 0 && (
              <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "0.5px solid #ccc", borderRadius: 6, zIndex: 10, maxHeight: 150, overflowY: "auto" }}>
                {suggestions.map(c => (
                  <div key={c.id}
                    onMouseDown={() => { setForm({ ...form, customer: c.first + " " + c.last, phone: c.phone || "", email: c.email || "", address: c.address || "", city: "" }); setShowSugg(false); }}
                    style={{ padding: "8px 10px", cursor: "pointer", fontSize: 13 }}
                    onMouseEnter={e => e.currentTarget.style.background = "#f5f5f5"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    {c.first} {c.last} <span style={{ color: "#888", fontSize: 12 }}>{c.phone}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </FormGroup>
        <FormGroup label="Telefon">
          <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="Auto-popunjeno ili unesi" />
        </FormGroup>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
        <FormGroup label="Datum">
          <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />
        </FormGroup>
        <FormGroup label="Popust %">
          <Input
            type="number" min="0" max="100" value={form.discount}
            onChange={e => setForm({ ...form, discount: parseFloat(e.target.value) || 0 })}
            placeholder="0"
          />
        </FormGroup>
      </div>
      <div style={{ marginBottom: 14 }}>
        <FormGroup label="Napomena">
          <Input value={form.napomena} onChange={e => setForm({ ...form, napomena: e.target.value })} placeholder="Opciono" />
        </FormGroup>
      </div>
      <ProductLineEditor lines={form.lines} setLines={lines => setForm({ ...form, lines })}
        orders={orders} />
      <PreviewBox rows={[
        { label: "Subtotal", value: subtotal.toLocaleString() + " RSD" },
        form.discount > 0 && { label: `Popust (${form.discount}%)`, value: "- " + Math.round(discountAmt).toLocaleString() + " RSD", color: "#E24B4A" },
        { label: "PDV (uključen 20%)", value: Math.round(pdv).toLocaleString() + " RSD", muted: true },
        { label: "Ukupno", value: total.toLocaleString() + " RSD", bold: true },
      ]} />
      <Modal open={showNewContact} onClose={() => setShowNewContact(false)} title="Dodaj kontakt"
        footer={<><Btn onClick={() => setShowNewContact(false)}>Otkaži</Btn><Btn variant="primary" onClick={saveNewContact}>Sačuvaj</Btn></>}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
          <FormGroup label="Ime"><Input value={newContact.first} onChange={e => setNewContact({ ...newContact, first: e.target.value })} placeholder="Marko" /></FormGroup>
          <FormGroup label="Prezime"><Input value={newContact.last} onChange={e => setNewContact({ ...newContact, last: e.target.value })} placeholder="Petrović" /></FormGroup>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
          <FormGroup label="Telefon"><Input value={newContact.phone} onChange={e => setNewContact({ ...newContact, phone: e.target.value })} placeholder="+381 60 123 4567" /></FormGroup>
          <FormGroup label="Email"><Input value={newContact.email} onChange={e => setNewContact({ ...newContact, email: e.target.value })} placeholder="marko@email.com" /></FormGroup>
        </div>
        <FormGroup label="Adresa"><Input value={newContact.address} onChange={e => setNewContact({ ...newContact, address: e.target.value })} placeholder="Ulica, Grad, Poštanski broj" /></FormGroup>
        <FormGroup label="Napomena"><Input value={newContact.note} onChange={e => setNewContact({ ...newContact, note: e.target.value })} placeholder="Opciono" /></FormGroup>
      </Modal>
    </Modal>
  );
}

// ── Merge Orders Modal ────────────────────────────────────────────────────────

function MergeOrdersModal({ open, selectedOrders, onClose, onConfirm }) {
  const first = selectedOrders[0] || {};
  const [customer, setCustomer] = useState(first.customer || "");
  const [address, setAddress] = useState(first.address || "");
  const [city, setCity] = useState(first.city || "");
  const [phone, setPhone] = useState(first.phone || "");
  const [email, setEmail] = useState(first.email || "");
  const [discount, setDiscount] = useState(0);

  useEffect(() => {
    if (open && selectedOrders.length > 0) {
      const f = selectedOrders[0];
      setCustomer(f.customer || "");
      setAddress(f.address || "");
      setCity(f.city || "");
      setPhone(f.phone || "");
      setEmail(f.email || "");
      setDiscount(0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const allLines = selectedOrders.flatMap(o =>
    o.type === "print"
      ? [{ product: "Print servis", color: "", size: "", dizajn: "", qty: o.printQty || 1, price: o.printPrice || 0, permalink: "" }]
      : (o.lines || [])
  );
  const subtotal = allLines.reduce((s, l) => s + (l.price || 0) * (l.qty || 1), 0);
  const total = Math.round(subtotal * (1 - discount / 100));

  const namesMatch = selectedOrders.every(o => o.customer === first.customer);
  const addressesMatch = selectedOrders.every(o => (o.address || "") === (first.address || ""));

  function handleConfirm() {
    onConfirm({ customer, address, city, phone, email, discount, lines: allLines, total });
  }

  return (
    <Modal open={open} onClose={onClose} title="Spoji porudžbine"
      footer={<><Btn onClick={onClose}>Otkaži</Btn><Btn variant="primary" onClick={handleConfirm} disabled={!customer}>Spoji</Btn></>}>
      {(!namesMatch || !addressesMatch) && (
        <div style={{ background: "#FFF3CD", border: "0.5px solid #FBBF24", borderRadius: 6, padding: "8px 12px", fontSize: 12, marginBottom: 12 }}>
          ⚠ Izabrane porudžbine imaju različite kupce ili adrese. Proveri podatke pre spajanja.
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
        <FormGroup label="Kupac"><Input value={customer} onChange={e => setCustomer(e.target.value)} /></FormGroup>
        <FormGroup label="Telefon"><Input value={phone} onChange={e => setPhone(e.target.value)} /></FormGroup>
        <FormGroup label="Adresa"><Input value={address} onChange={e => setAddress(e.target.value)} /></FormGroup>
        <FormGroup label="Grad"><Input value={city} onChange={e => setCity(e.target.value)} /></FormGroup>
        <FormGroup label="Email"><Input value={email} onChange={e => setEmail(e.target.value)} /></FormGroup>
        <FormGroup label="Popust (%)"><Input type="number" min="0" max="100" value={discount} onChange={e => setDiscount(parseFloat(e.target.value) || 0)} /></FormGroup>
      </div>

      <div style={{ fontSize: 11, fontWeight: 500, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Stavke</div>
      <div style={{ background: "#f7f7f7", borderRadius: 8, padding: "8px 12px", marginBottom: 12 }}>
        {allLines.map((l, i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "4px 0", borderBottom: i < allLines.length - 1 ? "0.5px solid #eee" : "none" }}>
            <span style={{ color: "#444" }}>{l.qty}× {l.product}{l.color ? ` (${l.color}${l.size ? `, ${l.size}` : ""})` : ""}{l.dizajn ? ` — ${l.dizajn}` : ""}</span>
            <span style={{ fontWeight: 500 }}>{(l.price * l.qty).toLocaleString()} RSD</span>
          </div>
        ))}
      </div>

      <PreviewBox rows={[
        { label: "Međuzbir", value: subtotal.toLocaleString() + " RSD" },
        ...(discount > 0 ? [{ label: `Popust (${discount}%)`, value: `−${(subtotal - total).toLocaleString()} RSD`, muted: true }] : []),
        { label: "Ukupno", value: total.toLocaleString() + " RSD", bold: true },
      ]} />

      <div style={{ fontSize: 12, color: "#aaa", marginTop: 8 }}>
        Originalne porudžbine ({selectedOrders.map(o => `#${o.id}`).join(", ")}) biće označene kao Otkazano.
      </div>
    </Modal>
  );
}

// ── Orders ────────────────────────────────────────────────────────────────────

function Orders({ orders, setOrders, contacts, setContacts, setPage, setDetailOrder, setDetailOrderBack, prodCosts, purchasePrices, marketingEntries, shippingCfg, noShippingIds }) {
  const [tab, setTab] = useState("all");
  const [showDirect, setShowDirect] = useState(false);
  const [showPrint, setShowPrint] = useState(false);
  const [showMerge, setShowMerge] = useState(false);
  const [editOrder, setEditOrder] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");
  const [printForm, setPrintForm] = useState({ name: "", date: today(), qty: 5, price: 800, note: "" });
  const [ordFromDate, setOrdFromDate] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); });
  const [ordToDate, setOrdToDate] = useState(today);
  const [currentPage, setCurrentPage] = useState(1);
  const PER_PAGE = 50;

  const filtered = (tab === "all" ? orders : orders.filter(o => o.type === tab))
    .filter(o => o.date >= ordFromDate && o.date <= ordToDate);

  useEffect(() => { setCurrentPage(1); }, [tab, ordFromDate, ordToDate]);

  function handleDirectSave(data) {
    let finalData = data;
    if (!data.customerId && data.customer) {
      const parts = data.customer.trim().split(/\s+/);
      const newContact = { id: Date.now(), first: parts[0] || "", last: parts.slice(1).join(" ") || "", phone: data.phone || "", email: data.email || "", address: data.address || "", note: "" };
      setContacts(prev => [...prev, newContact]);
      finalData = { ...data, customerId: newContact.id };
    }
    if (editOrder) {
      const updated = { ...editOrder, ...finalData };
      setOrders(orders.map(o => o.id === editOrder.id ? updated : o));
      pushDirectOrder(updated);
    } else {
      const newOrder = { id: Date.now(), type: "direct", status: "pending", shippingThreshold: shippingCfg.freeShippingThreshold, ...finalData };
      setOrders([newOrder, ...orders]);
      pushDirectOrder(newOrder);
      setTab("direct");
    }
    setEditOrder(null);
  }

  function setStatus(id, status) {
    const order = orders.find(o => o.id === id);
    setOrders(orders.map(o => o.id === id ? { ...o, status } : o));
    if (order && order.type !== "web") pushDirectOrder({ ...order, status });
    else pushStatusToServer(id, status);
  }

  useEffect(() => {
    fetchServerStatuses().then(statuses => {
      if (Object.keys(statuses).length === 0) return;
      setOrders(prev => prev.map(o => statuses[o.id] ? { ...o, status: statuses[o.id] } : o));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function syncFromWoo() {
    setSyncing(true);
    setSyncMsg("");
    try {
      const data = await apiFetch(`${API_BASE}/api/sync-woo`, { method: "POST" }).then(r => r.json());
      if (!data.success) throw new Error(data.error || "Greška pri sinhronizaciji");
      const { orders: normalized, unknownProducts } = data.delta.length > 0 ? normalizeWooOrders(data.delta) : { orders: [], unknownProducts: [] };
      if (normalized.length > 0) {
        setOrders(prev => {
          const next = [...prev];
          normalized.forEach(o => {
            const idx = next.findIndex(x => x.id === o.id);
            if (idx >= 0) next[idx] = { ...next[idx], ...o };
            else next.push({ ...o, shippingThreshold: shippingCfg.freeShippingThreshold });
          });
          return next.sort((a, b) => {
            const dateDiff = b.date.localeCompare(a.date);
            if (dateDiff !== 0) return dateDiff;
            const aTime = a.date_created || String(a.id);
            const bTime = b.date_created || String(b.id);
            return bTime.localeCompare(aTime);
          });
        });
      }
      const msg = data.delta.length === 0 ? "Nema novih promena." : `${data.delta.length} porudžbina ažurirano.`;
      setSyncMsg(unknownProducts?.length > 0 ? `${msg} ⚠ Novi proizvodi: ${unknownProducts.join(", ")}` : msg);
    } catch (err) {
      setSyncMsg("Greška: " + err.message);
    }
    setSyncing(false);
  }

  function addPrint() {
    const total = parseFloat((printForm.qty * printForm.price).toFixed(2));
    const newOrder = { id: Date.now(), customer: printForm.name, customerId: null, type: "print", lines: [], total, date: printForm.date, status: "pending", printQty: printForm.qty, printPrice: printForm.price, shippingThreshold: shippingCfg.freeShippingThreshold };
    setOrders([newOrder, ...orders]);
    pushDirectOrder(newOrder);
    setShowPrint(false);
  }

  const [mergeGroup, setMergeGroup] = useState([]);
  const [lastMerge, setLastMerge] = useState(null);

  const duplicateGroups = useMemo(() => {
    const active = filtered.filter(o => o.status !== "cancelled");
    const groups = {};
    active.forEach(o => {
      const name = (o.customer || "").trim().toLowerCase();
      const phone = (o.phone || "").trim().replace(/\s+/g, "");
      const id = name || phone;
      if (!id) return;
      const key = `${o.date}||${id}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(o);
    });
    return Object.values(groups).filter(g => g.length >= 2);
  }, [filtered]);

  const duplicateIds = useMemo(() => new Set(duplicateGroups.flat().map(o => o.id)), [duplicateGroups]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const paginated = filtered.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  const dailyRows = useMemo(() => {
    const result = [];
    let currentDate = null;
    let groupOrders = [];
    const flush = () => {
      if (!groupOrders.length) return;
      const active = groupOrders.filter(o => o.status !== "cancelled");
      result.push({ type: "summary", date: currentDate, count: active.length, total: active.reduce((s, o) => s + (o.total || 0), 0) });
      groupOrders = [];
    };
    paginated.forEach(o => {
      if (o.date !== currentDate) { flush(); currentDate = o.date; }
      result.push({ type: "order", data: o });
      groupOrders.push(o);
    });
    flush();
    return result;
  }, [paginated]);

  function handleMergeConfirm({ customer, address, city, phone, email, discount, lines, total }) {
    const mergedId = mergeGroup.map(o => o.id).join(" ");
    const mergedOrder = {
      id: mergedId, type: "web", status: "pending",
      customer, address, city, phone, email, discount, lines, total,
      date: today(), shippingThreshold: shippingCfg.freeShippingThreshold,
      mergedFrom: mergeGroup.map(o => o.id),
    };
    const snapshots = mergeGroup.map(o => ({ ...o }));
    mergeGroup.forEach(o => {
      if (o.type === "web") pushStatusToServer(o.id, "cancelled");
      else pushDirectOrder({ ...o, status: "cancelled" });
    });
    pushDirectOrder(mergedOrder);
    const mergeIds = new Set(mergeGroup.map(o => o.id));
    setOrders(prev => [mergedOrder, ...prev.map(o => mergeIds.has(o.id) ? { ...o, status: "cancelled" } : o)]);
    setLastMerge({ mergedOrder, snapshots });
    setMergeGroup([]);
    setShowMerge(false);
  }

  function handleUndoMerge() {
    if (!lastMerge) return;
    const { mergedOrder, snapshots } = lastMerge;
    removeDirectOrder(mergedOrder.id);
    snapshots.forEach(o => {
      if (o.type === "web") pushStatusToServer(o.id, o.status);
      else pushDirectOrder(o);
    });
    const restoredIds = new Set(snapshots.map(o => o.id));
    setOrders(prev =>
      prev
        .filter(o => o.id !== mergedOrder.id)
        .map(o => restoredIds.has(o.id) ? snapshots.find(s => s.id === o.id) : o)
    );
    setLastMerge(null);
  }

  const printTotal = printForm.qty * printForm.price;
  const printPdv = pdvFromPrice(printTotal);

  return (
    <div>
      {lastMerge && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#1D9E75", color: "#fff", borderRadius: 8, padding: "10px 16px", marginBottom: 14, fontSize: 13 }}>
          <span>Porudžbine spojene u #{lastMerge.mergedOrder.id}.</span>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={handleUndoMerge} style={{ background: "rgba(255,255,255,0.25)", border: "none", color: "#fff", borderRadius: 6, padding: "4px 12px", cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 500 }}>Poništi</button>
            <button onClick={() => setLastMerge(null)} style={{ background: "transparent", border: "none", color: "rgba(255,255,255,0.7)", cursor: "pointer", fontSize: 16, lineHeight: 1 }}>×</button>
          </div>
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontSize: 18, fontWeight: 500 }}>Porudžbine</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          <Btn size="sm" onClick={syncFromWoo} style={{ opacity: syncing ? 0.6 : 1 }}>
            {syncing ? "Sinhronizacija…" : "↓ WooCommerce"}
          </Btn>
          {syncMsg && <span style={{ fontSize: 12, color: syncMsg.startsWith("Greška") ? "#E24B4A" : "#1D9E75" }}>{syncMsg}</span>}
          <Btn size="sm" onClick={() => { setEditOrder(null); setShowDirect(true); }}>+ Direktna prodaja</Btn>
          <Btn size="sm" onClick={() => setShowPrint(true)}>+ Print servis</Btn>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        <Input type="date" value={ordFromDate} onChange={e => setOrdFromDate(e.target.value)} style={{ width: 140 }} />
        <span style={{ color: "#aaa" }}>—</span>
        <Input type="date" value={ordToDate} onChange={e => setOrdToDate(e.target.value)} style={{ width: 140 }} />
        <span style={{ fontSize: 12, color: "#aaa" }}>{filtered.length} porudžbina</span>
      </div>
      <div style={{ display: "flex", gap: 4, marginBottom: 14, background: brand.tabBg, padding: 4, borderRadius: 8, width: "fit-content" }}>
        {[["all","Sve"],["web","WooCommerce"],["direct","Direktne"],["print","Print servis"]].map(([t, l]) => (
          <button key={t} onClick={() => { setTab(t); setCurrentPage(1); }} style={{ padding: "5px 12px", borderRadius: 6, border: "none", background: tab === t ? brand.tabActiveBg : "transparent", color: tab === t ? brand.tabActiveColor : brand.tabInactiveColor, fontWeight: tab === t ? 500 : 400, cursor: "pointer", fontSize: 13, fontFamily: "inherit", boxShadow: tab === t ? "0 0 0 0.5px #ddd" : "none" }}>{l}</button>
        ))}
      </div>
      <ScrollTable heads={["#","Datum","Kupac","Tip","Poreklo","Stavke","Ukupno","PDV","Neto Dobit","Status","Akcije"]}>
        {dailyRows.map((row, idx) => {
          if (row.type === "summary") {
            return (
              <tr key={`sum-${row.date}-${idx}`} style={{ background: "#f5f5f5" }}>
                <td colSpan={6} style={{ padding: "5px 10px", fontSize: 12, color: "#888", borderBottom: "0.5px solid #eee", borderTop: "0.5px solid #e0e0e0" }}>
                  {row.date} — {row.count} {row.count === 1 ? "porudžbina" : "porudžbine"}
                </td>
                <td style={{ padding: "5px 10px", fontWeight: 600, fontSize: 13, whiteSpace: "nowrap", borderBottom: "0.5px solid #eee", borderTop: "0.5px solid #e0e0e0" }}>
                  {Math.round(row.total).toLocaleString()} RSD
                </td>
                <td colSpan={4} style={{ borderBottom: "0.5px solid #eee", borderTop: "0.5px solid #e0e0e0" }} />
              </tr>
            );
          }
          const o = row.data;
          const profit = calcOrderProfit(o, prodCosts, purchasePrices, marketingEntries, orders, { ...shippingCfg, noShipping: noShippingIds?.has(String(o.id)) });
          const items = o.type === "print" ? `${o.printQty}× print servis` : o.lines.map(l => `${l.qty}× ${l.product}`).join(", ");
          const group = duplicateGroups.find(g => g.some(x => x.id === o.id));
          return (
            <tr key={o.id} style={{ background: duplicateIds.has(o.id) ? "#fffbf0" : "transparent" }}>
              <TD><span style={{ fontWeight: 500, cursor: "pointer", color: "#E24B4A" }} onClick={() => { setDetailOrder(o); setDetailOrderBack("orders"); setPage("order-detail"); }}>#{o.id}</span></TD>
              <TD style={{ color: "#888", fontSize: 12 }}>{o.date}</TD>
              <TD>
                <div>{o.customer}</div>
                {o.type !== "web" && o.createdBy && (
                  <div style={{ fontSize: 11, color: "#aaa" }}>
                    {o.createdBy}{o.editedBy ? ` · izm. ${o.editedBy}` : ""}
                  </div>
                )}
              </TD>
              <TD><Badge type={o.type} /></TD>
              <TD style={{ color: "#888", fontSize: 12 }}>{o.origin || "—"}</TD>
              <TD style={{ color: "#888", fontSize: 12, maxWidth: 180, whiteSpace: "normal" }}>{items}</TD>
              <TD style={{ fontWeight: 500 }}>{o.total.toLocaleString()} RSD</TD>
              <TD style={{ color: "#534AB7" }}>{Math.round(profit.pdv).toLocaleString()} RSD</TD>
              <TD style={{ color: profit.netoDobit >= 0 ? "#1D9E75" : "#E24B4A", fontWeight: 500 }}>{Math.round(profit.netoDobit).toLocaleString()} RSD</TD>
              <TD><Badge type={o.status} /></TD>
              <TD>
                <div style={{ display: "flex", gap: 4 }}>
                  {o.type !== "print" && o.status !== "odštampano" && o.status !== "spakovano" && o.status !== "cancelled" && (
                    <Btn size="xs" onClick={() => { setEditOrder(o); setShowDirect(true); }}>Izmeni</Btn>
                  )}
                  {group && (
                    <Btn size="xs" variant="primary" onClick={() => { setMergeGroup(group); setShowMerge(true); }}>Spoji</Btn>
                  )}
                  {o.status !== "cancelled" && o.status !== "odštampano" && o.status !== "spakovano" && (
                    <Btn size="xs" variant="danger" onClick={() => setStatus(o.id, "cancelled")}>Otkaži</Btn>
                  )}
                  {o.status === "cancelled" && (
                    <Btn size="xs" onClick={() => setStatus(o.id, "pending")}>Poništi</Btn>
                  )}
                </div>
              </TD>
            </tr>
          );
        })}
      </ScrollTable>

      {totalPages > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 12 }}>
          <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
            style={{ padding: "4px 12px", borderRadius: 6, border: "0.5px solid #ddd", background: "#fff", cursor: currentPage === 1 ? "default" : "pointer", color: currentPage === 1 ? "#ccc" : "#444", fontFamily: "inherit", fontSize: 13 }}>←</button>
          <span style={{ fontSize: 13, color: "#666" }}>{currentPage} / {totalPages}</span>
          <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
            style={{ padding: "4px 12px", borderRadius: 6, border: "0.5px solid #ddd", background: "#fff", cursor: currentPage === totalPages ? "default" : "pointer", color: currentPage === totalPages ? "#ccc" : "#444", fontFamily: "inherit", fontSize: 13 }}>→</button>
        </div>
      )}

      <MergeOrdersModal open={showMerge} selectedOrders={mergeGroup} onClose={() => { setShowMerge(false); setMergeGroup([]); }} onConfirm={handleMergeConfirm} />

      <DirectSaleModal open={showDirect} onClose={() => { setShowDirect(false); setEditOrder(null); }} onSave={handleDirectSave} contacts={contacts} setContacts={setContacts} editOrder={editOrder} orders={orders} />

      <Modal open={showPrint} onClose={() => setShowPrint(false)} title="Novi print servis"
        footer={<><Btn onClick={() => setShowPrint(false)}>Otkaži</Btn><Btn variant="primary" onClick={addPrint}>Kreiraj</Btn></>}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
          <FormGroup label="Kupac"><Input value={printForm.name} onChange={e => setPrintForm({ ...printForm, name: e.target.value })} placeholder="Ime kupca" /></FormGroup>
          <FormGroup label="Datum"><Input type="date" value={printForm.date} onChange={e => setPrintForm({ ...printForm, date: e.target.value })} /></FormGroup>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
          <FormGroup label="Broj komada"><Input type="number" min="1" value={printForm.qty} onChange={e => setPrintForm({ ...printForm, qty: parseInt(e.target.value) || 1 })} /></FormGroup>
          <FormGroup label="Cena po komadu (RSD)"><Input type="number" value={printForm.price} onChange={e => setPrintForm({ ...printForm, price: parseFloat(e.target.value) || 0 })} /></FormGroup>
        </div>
        <FormGroup label="Napomena"><Input value={printForm.note} onChange={e => setPrintForm({ ...printForm, note: e.target.value })} placeholder="Npr. Logo na leđima" /></FormGroup>
        <PreviewBox rows={[
          { label: "Stavke", value: `${printForm.qty} × ${printForm.price.toLocaleString()} RSD` },
          { label: "PDV (uključen)", value: Math.round(printPdv).toLocaleString() + " RSD", muted: true },
          { label: "Ukupno", value: printTotal.toLocaleString() + " RSD", bold: true },
        ]} />
      </Modal>

    </div>
  );
}

// ── Order Detail ──────────────────────────────────────────────────────────────

function OrderDetail({ order, onBack, onEdit, onCancel, onUncancel, onComplete, prodCosts, purchasePrices, marketingEntries, allOrders, shippingCfg, noShippingIds }) {
  if (!order) return null;
  const profit = calcOrderProfit(order, prodCosts, purchasePrices, marketingEntries, allOrders, { ...shippingCfg, noShipping: noShippingIds?.has(String(order.id)) });
  return (
    <div>
      <div onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", color: "#888", fontSize: 13, marginBottom: 16 }}>← Nazad na porudžbine</div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 18, fontWeight: 500 }}>Porudžbina #{order.id}</span>
          <Badge type={order.type} /><Badge type={order.status} />
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {order.type !== "print" && order.status !== "odštampano" && order.status !== "spakovano" && order.status !== "cancelled" && <Btn onClick={onEdit}>Izmeni</Btn>}
          {order.status !== "cancelled" && order.status !== "odštampano" && order.status !== "spakovano" && order.status !== "completed" && <Btn variant="danger" onClick={onCancel}>Otkaži</Btn>}
          {order.status === "cancelled" && <Btn onClick={onUncancel}>Poništi</Btn>}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: 10, marginBottom: 16 }}>
        <MetricCard label="Ukupno" value={order.total.toLocaleString() + " RSD"} />
        <MetricCard label="PDV (20%)" value={Math.round(profit.pdv).toLocaleString() + " RSD"} color="#534AB7" />
        <MetricCard label="Marketing (deo)" value={Math.round(profit.promo || 0).toLocaleString() + " RSD"} color="#854F0B" />
        <MetricCard label="Neto Dobit" value={Math.round(profit.netoDobit).toLocaleString() + " RSD"} color={profit.netoDobit >= 0 ? "#1D9E75" : "#E24B4A"} />
        <MetricCard label="% Zarade" value={profit.pctZarade.toFixed(1) + "%"} color={profit.pctZarade >= 0 ? "#1D9E75" : "#E24B4A"} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
        {[["Kupac", order.customer], ["Datum", order.date], order.discount ? ["Popust", order.discount + "%"] : null].filter(Boolean).map(([l, v]) => (
          <div key={l}><label style={{ fontSize: 11, color: "#888", display: "block", marginBottom: 3 }}>{l}</label><span style={{ fontSize: 14 }}>{v}</span></div>
        ))}
      </div>
      <div style={{ marginBottom: 16 }}>
        {order.type === "print"
          ? <div style={{ background: "#f5f5f5", borderRadius: 8, padding: 12, fontSize: 13, display: "flex", justifyContent: "space-between" }}><span>Print servis</span><span>{order.printQty} kom × {order.printPrice.toLocaleString()} RSD</span></div>
          : order.lines.map((l, i) => {
            const pp = getPurchasePrice(l.product, l.color, purchasePrices, order.date);
            const applicableCosts = SIMPLE_PRODUCTS.includes(l.product) ? {} : PRODUCT_COST_KEYS[l.product] ? Object.fromEntries(Object.entries(prodCosts).filter(([k]) => PRODUCT_COST_KEYS[l.product].includes(k))) : prodCosts;
      const r = calcItemProfit(l.price, pp, applicableCosts);
            return (
              <div key={i} style={{ padding: "10px 0", borderBottom: "0.5px solid #eee" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <div style={{ fontWeight: 500, fontSize: 13 }}>{l.product}</div>
                    <div style={{ fontSize: 12, color: "#888" }}>
                      {!NO_VARIANTS.includes(l.product) && `Boja: ${l.color} · Vel: ${l.size} · `}
                      {l.dizajn ? `Dizajn: ${l.dizajn}` : "Bez dizajna"}
                    </div>
                    <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>
                      Nab. cena: {pp.toLocaleString()} · Proiz. troškovi: {Math.round(totalProdCost(pp, getApplicableCosts(l.product, prodCosts)) - pp).toLocaleString()} · UPC: {Math.round(totalProdCost(pp, getApplicableCosts(l.product, prodCosts))).toLocaleString()} RSD
                    </div>
                  </div>
                  <div style={{ textAlign: "right", fontSize: 13 }}>
                    <div>{l.qty} × {l.price.toLocaleString()} RSD</div>
                    <div style={{ fontWeight: 500 }}>{(l.qty * l.price).toLocaleString()} RSD</div>
                    <div style={{ fontSize: 11, color: r.netoDobit >= 0 ? "#1D9E75" : "#E24B4A" }}>Dobit: {Math.round(r.netoDobit * l.qty).toLocaleString()} RSD</div>
                  </div>
                </div>
              </div>
            );
          })}
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <PreviewBox rows={[
          { label: "Prodajna cena", value: order.total.toLocaleString() + " RSD" },
          order.discount > 0 && { label: `Popust (${order.discount}%)`, value: "- " + Math.round(order.lines.reduce((s, l) => s + l.price * l.qty, 0) * (order.discount / 100)).toLocaleString() + " RSD", color: "#E24B4A" },
          { label: "Ukupna proiz. cena", value: Math.round(order.lines.reduce((s, l) => s + totalProdCost(getPurchasePrice(l.product, l.color, purchasePrices, order.date), getApplicableCosts(l.product, prodCosts)) * l.qty, 0)).toLocaleString() + " RSD", muted: true },
          { label: "Dobit + PDV", value: Math.round(order.total - order.lines.reduce((s, l) => s + totalProdCost(getPurchasePrice(l.product, l.color, purchasePrices, order.date), getApplicableCosts(l.product, prodCosts)) * l.qty, 0)).toLocaleString() + " RSD", muted: true },
          { label: "PDV (20%)", value: Math.round(profit.pdv).toLocaleString() + " RSD", muted: true },
          { label: "Marketing (deo)", value: Math.round(profit.promo || 0).toLocaleString() + " RSD", muted: true },
          { label: "Neto Dobit", value: Math.round(profit.netoDobit).toLocaleString() + " RSD", bold: true, color: profit.netoDobit >= 0 ? "#1D9E75" : "#E24B4A" },
        ]} />
      </div>
    </div>
  );
}

// ── Za štampu ─────────────────────────────────────────────────────────────────

function ZaStampu({ orders, setOrders, ordersReady }) {
  const [printStatuses, setPrintStatuses] = useState(() => buildPrintStatuses(orders));
  const [psLoaded, setPsLoaded] = useState(false);

  useEffect(() => {
    fetchPrintStatuses().then(serverPs => {
      setPrintStatuses(prev => ({ ...buildPrintStatuses(orders), ...prev, ...serverPs }));
      setPsLoaded(true);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!psLoaded) return;
    const derived = buildPrintStatuses(orders);
    setPrintStatuses(prev => {
      const missing = Object.fromEntries(Object.entries(derived).filter(([k]) => !(k in prev)));
      return Object.keys(missing).length ? { ...prev, ...missing } : prev;
    });
  }, [orders, psLoaded]);

  const activeOrders = !ordersReady ? [] : orders.filter(o => o.status !== "cancelled" && o.status !== "poslato" && o.lines && o.lines.length > 0);

  function nextStatus(s) {
    if (s === "pending") return "odštampano";
    return "pending";
  }

  function setItemStatus(orderId, lineIdx, itemIdx, status) {
    const key = `${orderId}-${lineIdx}-${itemIdx}`;
    const next = { ...printStatuses, [key]: status };
    setPrintStatuses(next);
    pushPrintStatus(key, status);
    const order = orders.find(o => o.id === orderId);
    if (!order) return;
    const items = order.lines.flatMap((l, li) => Array.from({ length: l.qty }, (_, ii) => next[`${orderId}-${li}-${ii}`] || "pending"));
    const allPrinted = items.every(s => s === "odštampano");
    if (allPrinted) {
      const upd = { ...order, status: "odštampano" };
      setOrders(prev => prev.map(o => o.id === orderId ? upd : o));
      if (order.type !== "web") pushDirectOrder(upd); else pushStatusToServer(orderId, "odštampano");
    } else if (order.status === "odštampano") {
      const upd = { ...order, status: "in progress" };
      setOrders(prev => prev.map(o => o.id === orderId ? upd : o));
      if (order.type !== "web") pushDirectOrder(upd); else pushStatusToServer(orderId, "in progress");
    }
  }

  function isOrderDone(order) {
    return order.lines.every((l, li) =>
      Array.from({ length: l.qty }, (_, ii) => printStatuses[`${order.id}-${li}-${ii}`] === "odštampano").every(Boolean)
    );
  }

  return (
    <div>
      <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 16 }}>Za štampu</div>
      {!ordersReady && <div style={{ color: "#aaa", fontSize: 13, padding: 20, textAlign: "center" }}>Učitavanje…</div>}
      {ordersReady && activeOrders.length === 0 && <div style={{ color: "#aaa", fontSize: 13, padding: 20, textAlign: "center" }}>Nema aktivnih porudžbina za štampu.</div>}
      {activeOrders.map(order => (
        <SectionCard key={order.id} title={null}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontWeight: 500, fontSize: 15 }}>Porudžbina #{order.id}</span>
              <Badge type={order.status} />
              {isOrderDone(order) && <span style={{ background: "#EAF3DE", color: "#3B6D11", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 500 }}>Sve odštampano ✓</span>}
            </div>
            <div style={{ fontSize: 13, color: "#888", textAlign: "right" }}>
              <div>{order.customer} · {order.date}</div>
              {order.address && <div style={{ fontSize: 12, marginTop: 2 }}>{order.address}</div>}
            </div>
          </div>
          {order.napomena && <div style={{ fontSize: 12, color: "#854F0B", background: "#FAEEDA", borderRadius: 6, padding: "6px 10px", marginBottom: 10 }}>📝 {order.napomena}</div>}
          {order.lines.map((l, li) =>
            Array.from({ length: l.qty }, (_, ii) => {
              const key = `${order.id}-${li}-${ii}`;
              const status = printStatuses[key] || "pending";
              return (
                <div key={key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: "0.5px solid #eee" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {(() => {
                      const permalink = l.permalink || resolveDesignPermalink(l.product, l.dizajn);
                      const colorSlug = l.colorSlug || COLOR_URL_SLUGS[l.color];
                      const href = permalink
                        ? `${permalink}${colorSlug ? `?attribute_pa_boja=${colorSlug}` : ""}`
                        : resolveLineImageUrl(l, orders);
                      if (!href) return null;
                      return <a href={href} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: "#666", whiteSpace: "nowrap" }}>slika</a>;
                    })()}
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{l.product}</div>
                      <div style={{ fontSize: 12, color: "#888" }}>
                        {!NO_VARIANTS.includes(l.product) && `${l.color} · ${l.size}`}
                        {l.dizajn && ` · ${l.dizajn}`}
                        {l.qty > 1 && <span style={{ color: "#aaa" }}> (kom {ii + 1}/{l.qty})</span>}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {status === "pending"      && <span style={{ background: "#E6F1FB", color: "#185FA5",  padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 500 }}>Na čekanju</span>}
                    {status === "odštampano"   && <span style={{ background: "#EAF3DE", color: "#3B6D11",  padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 500 }}>Odštampano ✓</span>}
                    <Btn size="xs"
                      variant={status === "pending" ? "success" : undefined}
                      onClick={() => setItemStatus(order.id, li, ii, nextStatus(status))}>
                      {status === "pending" ? "Odštampano" : "Poništi"}
                    </Btn>
                  </div>
                </div>
              );
            })
          )}
        </SectionCard>
      ))}
    </div>
  );
}

// ── Expenses ──────────────────────────────────────────────────────────────────

function Expenses({ expenses, setExpenses, marketingEntries, setMarketingEntries, marketingChannels, setMarketingChannels, expenseCategories, setExpenseCategories, fromDate, toDate, setFromDate, setToDate, shippingCfg, setShippingCfg }) {
  const [tab, setTab] = useState("expenses");
  const [showExpModal, setShowExpModal] = useState(false);
  const [showMktModal, setShowMktModal] = useState(false);
  const [showChannelModal, setShowChannelModal] = useState(false);
  const [newChannel, setNewChannel] = useState("");
  const [expForm, setExpForm] = useState({ date: today(), amount: "", category: "Prints", note: "" });
  const [editExp, setEditExp] = useState(null);
  const [addingNewCat, setAddingNewCat] = useState(false);
  const [addingNewCatEdit, setAddingNewCatEdit] = useState(false);
  const [editMkt, setEditMkt] = useState(null);

  const lastMktAmounts = {};
  marketingChannels.forEach(ch => {
    const entries = [...marketingEntries].filter(e => e.channel === ch).sort((a, b) => b.date.localeCompare(a.date));
    if (entries.length) lastMktAmounts[ch] = entries[0].amount;
  });
  const [mktForm, setMktForm] = useState({ fromDate: today(), toDate: today(), channel: marketingChannels[0] || "", amount: lastMktAmounts[marketingChannels[0]] || 0 });

  const fExp = expenses.filter(e => e.date >= fromDate && e.date <= toDate);
  const fMkt = marketingEntries.filter(e => e.date >= fromDate && e.date <= toDate);
  const expCats = {};
  let expTotal = 0;
  fExp.forEach(e => { expCats[e.category] = (expCats[e.category] || 0) + e.amount; expTotal += e.amount; });
  const mktByChannel = {};
  let mktTotal = 0;
  fMkt.forEach(e => { mktByChannel[e.channel] = (mktByChannel[e.channel] || 0) + e.amount; mktTotal += e.amount; });

  function saveExp() {
    const amount = parseFloat(expForm.amount);
    if (!amount || !expForm.category) return;
    const id = Date.now();
    const entry = { id, ...expForm, amount, createdBy: getAuthUsername(), createdAt: new Date().toISOString() };
    if (!expenseCategories.includes(entry.category)) {
      setExpenseCategories(prev => [...prev, entry.category]);
    }
    setExpenses([entry, ...expenses]);
    logActivity("created", "expense", id, `${entry.category} — ${entry.note || entry.amount + " RSD"}`);
    setExpForm({ date: today(), amount: "", category: expenseCategories[0] || "Prints", note: "" });
    setShowExpModal(false);
    setAddingNewCat(false);
  }

  function saveEditExp() {
    const amount = parseFloat(editExp.amount);
    if (!amount || !editExp.category) return;
    const updated = { ...editExp, amount, editedBy: getAuthUsername(), editedAt: new Date().toISOString() };
    if (!expenseCategories.includes(updated.category)) {
      setExpenseCategories(prev => [...prev, updated.category]);
    }
    setExpenses(expenses.map(e => e.id === editExp.id ? updated : e));
    logActivity("edited", "expense", editExp.id, `${updated.category} — ${updated.note || updated.amount + " RSD"}`);
    setEditExp(null);
    setAddingNewCatEdit(false);
  }

  function deleteExp(id) {
    const e = expenses.find(x => x.id === id);
    setExpenses(expenses.filter(e => e.id !== id));
    if (e) logActivity("deleted", "expense", id, `${e.category} — ${e.note || e.amount + " RSD"}`);
  }

  function saveMkt() {
    const amount = parseFloat(mktForm.amount);
    if (!amount || !mktForm.fromDate || !mktForm.toDate) return;
    const entries = [];
    const cur = new Date(mktForm.fromDate);
    const end = new Date(mktForm.toDate);
    while (cur <= end) {
      const date = cur.toISOString().split("T")[0];
      entries.push({ id: Date.now() + entries.length, date, channel: mktForm.channel, amount, createdBy: getAuthUsername(), createdAt: new Date().toISOString() });
      cur.setDate(cur.getDate() + 1);
    }
    setMarketingEntries([...entries, ...marketingEntries]);
    logActivity("created", "marketing", entries[0]?.id, `${mktForm.channel} ${mktForm.fromDate}${mktForm.fromDate !== mktForm.toDate ? "→" + mktForm.toDate : ""} ${amount} RSD`);
    setShowMktModal(false);
  }

  function addChannel() {
    if (!newChannel.trim() || marketingChannels.includes(newChannel.trim())) return;
    setMarketingChannels([...marketingChannels, newChannel.trim()]);
    setNewChannel("");
    setShowChannelModal(false);
  }

  function removeChannel(ch) {
    setMarketingChannels(marketingChannels.filter(c => c !== ch));
    setMarketingEntries(marketingEntries.filter(e => e.channel !== ch));
  }

  function saveEditMkt() {
    const amount = parseFloat(editMkt.amount);
    if (!amount) return;
    const updated = { ...editMkt, amount, editedBy: getAuthUsername(), editedAt: new Date().toISOString() };
    setMarketingEntries(marketingEntries.map(e => e.id === editMkt.id ? updated : e));
    logActivity("edited", "marketing", editMkt.id, `${updated.channel} ${updated.date} ${amount} RSD`);
    setEditMkt(null);
  }

  function deleteMkt(id) {
    const e = marketingEntries.find(x => x.id === id);
    setMarketingEntries(marketingEntries.filter(e => e.id !== id));
    if (e) logActivity("deleted", "marketing", id, `${e.channel} ${e.date} ${e.amount} RSD`);
  }

  const dateFilter = (
    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
      <label style={{ fontSize: 12, color: "#888" }}>Od</label>
      <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={{ width: 150 }} />
      <label style={{ fontSize: 12, color: "#888" }}>Do</label>
      <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={{ width: 150 }} />
      <Btn size="sm">Primeni</Btn>
    </div>
  );

  function exportTroskovi() {
    const expRows = [...fExp].sort((a, b) => b.date.localeCompare(a.date)).map(e => ({
      "Datum":           e.date,
      "Kategorija":      e.category,
      "Napomena":        e.note || "",
      "Iznos (RSD)":     e.amount,
      "Uneo":            e.createdBy || "",
      "Izmenio":         e.editedBy || "",
    }));
    const mktRows = [...fMkt].sort((a, b) => b.date.localeCompare(a.date)).map(e => ({
      "Datum":           e.date,
      "Kanal":           e.channel,
      "Iznos (RSD)":     e.amount,
      "Uneo":            e.createdBy || "",
      "Izmenio":         e.editedBy || "",
    }));
    downloadXLSX([
      ["Troškovi",    buildSheet(expRows, [13, 16, 32, 14, 14, 14])],
      ["Marketing",   buildSheet(mktRows, [13, 18, 14, 14, 14])],
    ], `Troskovi_${fromDate}_${toDate}.xlsx`);
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div style={{ fontSize: 18, fontWeight: 500 }}>Troškovi</div>
        <Btn size="sm" onClick={exportTroskovi}>⬇ Preuzmi izveštaj</Btn>
      </div>
      <div style={{ display: "flex", gap: 4, marginBottom: 14, background: brand.tabBg, padding: 4, borderRadius: 8, width: "fit-content" }}>
        {[["expenses","Troškovi"],["marketing","Marketing kanali"]].map(([t, l]) => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: "5px 14px", borderRadius: 6, border: "none", background: tab === t ? brand.tabActiveBg : "transparent", color: tab === t ? brand.tabActiveColor : brand.tabInactiveColor, fontWeight: tab === t ? 500 : 400, cursor: "pointer", fontSize: 13, fontFamily: "inherit", boxShadow: tab === t ? "0 0 0 0.5px #ddd" : "none" }}>{l}</button>
        ))}
      </div>

      {tab === "expenses" && (
        <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
            {dateFilter}
            <Btn variant="primary" size="sm" onClick={() => setShowExpModal(true)}>+ Dodaj trošak</Btn>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: 10, marginBottom: 16 }}>
            <MetricCard label="Ukupni troškovi" value={expTotal.toLocaleString() + " RSD"} color="#E24B4A" />
            {Object.entries(expCats).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([cat, amt]) => <MetricCard key={cat} label={cat} value={amt.toLocaleString() + " RSD"} />)}
          </div>
          <ScrollTable heads={["Datum","Kategorija","Napomena","Iznos",""]}>
            {fExp.map(e => (
              <tr key={e.id}>
                <TD>{e.date}</TD>
                <TD><span style={{ background: "#FAEEDA", color: "#854F0B", padding: "3px 8px", borderRadius: 20, fontSize: 11, fontWeight: 500 }}>{e.category}</span></TD>
                <TD style={{ color: "#888" }}>
                  <div>{e.note || "—"}</div>
                  {e.createdBy && <div style={{ fontSize: 11, color: "#bbb" }}>{e.createdBy}{e.editedBy ? ` · izm. ${e.editedBy}` : ""}</div>}
                </TD>
                <TD style={{ fontWeight: 500 }}>{e.amount.toLocaleString()} RSD</TD>
                <TD>
                  <div style={{ display: "flex", gap: 4 }}>
                    <Btn size="xs" onClick={() => setEditExp({ ...e, amount: String(e.amount) })}>Izmeni</Btn>
                    <Btn size="xs" variant="danger" onClick={() => deleteExp(e.id)}>Obriši</Btn>
                  </div>
                </TD>
              </tr>
            ))}
          </ScrollTable>
        </>
      )}

      {tab === "marketing" && (
        <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
            {dateFilter}
            <div style={{ display: "flex", gap: 6 }}>
              <Btn size="sm" onClick={() => setShowChannelModal(true)}>+ Kanal</Btn>
              <Btn variant="primary" size="sm" onClick={() => {
                setMktForm({ fromDate: today(), toDate: today(), channel: marketingChannels[0] || "", amount: lastMktAmounts[marketingChannels[0]] || 0 });
                setShowMktModal(true);
              }}>+ Unesi trošak</Btn>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: 10, marginBottom: 16 }}>
            <MetricCard label="Ukupni marketing" value={mktTotal.toLocaleString() + " RSD"} color="#854F0B" />
            {Object.entries(mktByChannel).sort((a, b) => b[1] - a[1]).map(([ch, amt]) => <MetricCard key={ch} label={ch} value={amt.toLocaleString() + " RSD"} />)}
          </div>
          <SectionCard title="Aktivni kanali">
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {marketingChannels.map(ch => (
                <div key={ch} style={{ display: "flex", alignItems: "center", gap: 6, background: "#f5f5f5", borderRadius: 8, padding: "6px 12px", fontSize: 13 }}>
                  <span>{ch}</span>
                  <button onClick={() => removeChannel(ch)} style={{ background: "none", border: "none", color: "#E24B4A", cursor: "pointer", fontSize: 14, lineHeight: 1 }}>×</button>
                </div>
              ))}
              {marketingChannels.length === 0 && <div style={{ color: "#aaa", fontSize: 13 }}>Nema kanala. Dodaj prvi.</div>}
            </div>
          </SectionCard>
          <ScrollTable heads={["Datum","Kanal","Iznos",""]}>
            {[...fMkt].sort((a, b) => b.date.localeCompare(a.date)).map(e => (
              <tr key={e.id}>
                <TD>{e.date}</TD>
                <TD>
                  <span style={{ background: "#FAEEDA", color: "#854F0B", padding: "3px 8px", borderRadius: 20, fontSize: 11, fontWeight: 500 }}>{e.channel}</span>
                  {e.createdBy && <div style={{ fontSize: 11, color: "#bbb", marginTop: 2 }}>{e.createdBy}{e.editedBy ? ` · izm. ${e.editedBy}` : ""}</div>}
                </TD>
                <TD style={{ fontWeight: 500 }}>{e.amount.toLocaleString()} RSD</TD>
                <TD>
                  <div style={{ display: "flex", gap: 4 }}>
                    <Btn size="xs" onClick={() => setEditMkt({ ...e, amount: String(e.amount) })}>Izmeni</Btn>
                    <Btn size="xs" variant="danger" onClick={() => deleteMkt(e.id)}>Obriši</Btn>
                  </div>
                </TD>
              </tr>
            ))}
          </ScrollTable>
        </>
      )}

      <Modal open={showExpModal} onClose={() => setShowExpModal(false)} title="Dodaj trošak"
        footer={<><Btn onClick={() => setShowExpModal(false)}>Otkaži</Btn><Btn variant="primary" onClick={saveExp}>Sačuvaj</Btn></>}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
          <FormGroup label="Datum"><Input type="date" value={expForm.date} onChange={e => setExpForm({ ...expForm, date: e.target.value })} /></FormGroup>
          <FormGroup label="Iznos (RSD)"><Input type="number" step="1" value={expForm.amount} onChange={e => setExpForm({ ...expForm, amount: e.target.value })} placeholder="0" /></FormGroup>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <FormGroup label="Kategorija">
            {addingNewCat ? (
              <div style={{ display: "flex", gap: 6 }}>
                <Input autoFocus value={expForm.category} onChange={e => setExpForm({ ...expForm, category: e.target.value })} placeholder="Nova kategorija" />
                <Btn size="xs" onClick={() => { setAddingNewCat(false); setExpForm({ ...expForm, category: expenseCategories[0] || "Prints" }); }}>✕</Btn>
              </div>
            ) : (
              <Select value={expForm.category} onChange={e => {
                if (e.target.value === "__new__") { setAddingNewCat(true); setExpForm({ ...expForm, category: "" }); }
                else setExpForm({ ...expForm, category: e.target.value });
              }}>
                {expenseCategories.map(c => <option key={c}>{c}</option>)}
                <option value="__new__">— Unesi novu…</option>
              </Select>
            )}
          </FormGroup>
          <FormGroup label="Napomena"><Input value={expForm.note} onChange={e => setExpForm({ ...expForm, note: e.target.value })} placeholder="Opciono" /></FormGroup>
        </div>
      </Modal>

      <Modal open={!!editExp} onClose={() => setEditExp(null)} title="Izmeni trošak"
        footer={<><Btn onClick={() => setEditExp(null)}>Otkaži</Btn><Btn variant="primary" onClick={saveEditExp}>Sačuvaj</Btn></>}>
        {editExp && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
              <FormGroup label="Datum"><Input type="date" value={editExp.date} onChange={e => setEditExp({ ...editExp, date: e.target.value })} /></FormGroup>
              <FormGroup label="Iznos (RSD)"><Input type="number" step="1" value={editExp.amount} onChange={e => setEditExp({ ...editExp, amount: e.target.value })} /></FormGroup>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <FormGroup label="Kategorija">
                {addingNewCatEdit ? (
                  <div style={{ display: "flex", gap: 6 }}>
                    <Input autoFocus value={editExp.category} onChange={e => setEditExp({ ...editExp, category: e.target.value })} placeholder="Nova kategorija" />
                    <Btn size="xs" onClick={() => { setAddingNewCatEdit(false); setEditExp({ ...editExp, category: expenseCategories[0] || "Prints" }); }}>✕</Btn>
                  </div>
                ) : (
                  <Select value={editExp.category} onChange={e => {
                    if (e.target.value === "__new__") { setAddingNewCatEdit(true); setEditExp({ ...editExp, category: "" }); }
                    else setEditExp({ ...editExp, category: e.target.value });
                  }}>
                    {expenseCategories.map(c => <option key={c} value={c}>{c}</option>)}
                    <option value="__new__">— Unesi novu…</option>
                  </Select>
                )}
              </FormGroup>
              <FormGroup label="Napomena"><Input value={editExp.note} onChange={e => setEditExp({ ...editExp, note: e.target.value })} /></FormGroup>
            </div>
          </>
        )}
      </Modal>

      <Modal open={showMktModal} onClose={() => setShowMktModal(false)} title="Unesi marketing trošak"
        footer={<><Btn onClick={() => setShowMktModal(false)}>Otkaži</Btn><Btn variant="primary" onClick={saveMkt}>Sačuvaj</Btn></>}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
          <FormGroup label="Od"><Input type="date" value={mktForm.fromDate} onChange={e => setMktForm({ ...mktForm, fromDate: e.target.value })} /></FormGroup>
          <FormGroup label="Do"><Input type="date" value={mktForm.toDate} onChange={e => setMktForm({ ...mktForm, toDate: e.target.value })} /></FormGroup>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
          <FormGroup label="Kanal">
            <Select value={mktForm.channel} onChange={e => {
              const last = lastMktAmounts[e.target.value] || 0;
              setMktForm({ ...mktForm, channel: e.target.value, amount: last });
            }}>
              {marketingChannels.map(c => <option key={c}>{c}</option>)}
            </Select>
          </FormGroup>
          <FormGroup label="Dnevni budžet (RSD)">
            <Input type="number" step="1" value={mktForm.amount} onChange={e => setMktForm({ ...mktForm, amount: parseFloat(e.target.value) || 0 })} />
          </FormGroup>
        </div>
        {mktForm.fromDate && mktForm.toDate && mktForm.fromDate <= mktForm.toDate && (
          <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
            {Math.round((new Date(mktForm.toDate) - new Date(mktForm.fromDate)) / 86400000) + 1} dana ·{" "}
            Ukupno: {((Math.round((new Date(mktForm.toDate) - new Date(mktForm.fromDate)) / 86400000) + 1) * (parseFloat(mktForm.amount) || 0)).toLocaleString()} RSD
          </div>
        )}
      </Modal>

      <Modal open={showChannelModal} onClose={() => setShowChannelModal(false)} title="Dodaj marketing kanal"
        footer={<><Btn onClick={() => setShowChannelModal(false)}>Otkaži</Btn><Btn variant="primary" onClick={addChannel}>Dodaj</Btn></>}>
        <FormGroup label="Naziv kanala">
          <Input value={newChannel} onChange={e => setNewChannel(e.target.value)} placeholder="Npr. TikTok, Google, Facebook…" />
        </FormGroup>
      </Modal>

      <Modal open={!!editMkt} onClose={() => setEditMkt(null)} title="Izmeni marketing trošak"
        footer={<><Btn onClick={() => setEditMkt(null)}>Otkaži</Btn><Btn variant="primary" onClick={saveEditMkt}>Sačuvaj</Btn></>}>
        {editMkt && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <FormGroup label="Datum"><Input type="date" value={editMkt.date} onChange={e => setEditMkt({ ...editMkt, date: e.target.value })} /></FormGroup>
            <FormGroup label="Iznos (RSD)"><Input type="number" step="1" value={editMkt.amount} onChange={e => setEditMkt({ ...editMkt, amount: e.target.value })} /></FormGroup>
            <FormGroup label="Kanal">
              <Select value={editMkt.channel} onChange={e => setEditMkt({ ...editMkt, channel: e.target.value })}>
                {marketingChannels.map(c => <option key={c}>{c}</option>)}
              </Select>
            </FormGroup>
          </div>
        )}
      </Modal>

      <SectionCard title={null}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ fontSize: 13, color: "#444", whiteSpace: "nowrap" }}>Besplatna dostava — min. porudžbina</div>
          <Input type="number" value={shippingCfg.freeShippingThreshold} style={{ width: 120 }}
            onChange={e => setShippingCfg({ ...shippingCfg, freeShippingThreshold: parseFloat(e.target.value) || 0 })} />
          <div style={{ fontSize: 12, color: "#aaa" }}>RSD</div>
        </div>
      </SectionCard>
    </div>
  );
}

// ── CRM ───────────────────────────────────────────────────────────────────────

function CRM({ contacts, setContacts, orders, onViewContact }) {
  const [showContact, setShowContact] = useState(false);
  const [newContact, setNewContact] = useState({ first: "", last: "", phone: "", email: "", address: "", note: "" });
  const [sortBy, setSortBy] = useState("spent");
  const [search, setSearch] = useState("");

  const data = contacts.map(c => {
    const co = orders.filter(o => (o.customerId === c.id || (c.email && o.email === c.email)) && o.status !== "cancelled");
    const total = co.reduce((s, o) => s + o.total, 0);
    const lastOrderDate = co.length ? [...co].sort((a, b) => b.date.localeCompare(a.date))[0].date : "—";
    return { ...c, orderCount: co.length, total, lastOrderDate };
  }).sort((a, b) => {
    if (sortBy === "date") {
      if (a.lastOrderDate === "—" && b.lastOrderDate === "—") return 0;
      if (a.lastOrderDate === "—") return 1;
      if (b.lastOrderDate === "—") return -1;
      return b.lastOrderDate.localeCompare(a.lastOrderDate);
    }
    return b.total - a.total;
  });

  const tierBg = ["#FAEEDA","#FAEEDA","#D3D1C7","#D3D1C7","#F5C4B3","#F5C4B3"];
  const tierFg = ["#854F0B","#854F0B","#444441","#444441","#712B13","#712B13"];
  const tierLabel = ["Gold","Gold","Silver","Silver","Bronze","Bronze"];

  function saveContact() {
    if (!newContact.first && !newContact.last) return;
    setContacts(prev => [...prev, { id: Date.now(), ...newContact }]);
    setNewContact({ first: "", last: "", phone: "", email: "", address: "", note: "" });
    setShowContact(false);
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ fontSize: 18, fontWeight: 500 }}>Kupci</div>
          <div style={{ display: "flex", gap: 2, background: "#f5f5f5", borderRadius: 8, padding: 3 }}>
            {[["spent", "Ukupno potrošeno"], ["date", "Datum"]].map(([k, l]) => (
              <button key={k} onClick={() => setSortBy(k)} style={{ padding: "4px 10px", borderRadius: 6, border: "none", background: sortBy === k ? "#fff" : "transparent", fontWeight: sortBy === k ? 500 : 400, cursor: "pointer", fontSize: 12, fontFamily: "inherit", boxShadow: sortBy === k ? "0 0 0 0.5px #ddd" : "none" }}>{l}</button>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <Btn size="sm" onClick={() => setShowContact(true)}>+ Kontakt</Btn>
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <Input placeholder="Pretraži kupce…" value={search} onChange={e => setSearch(e.target.value)} style={{ maxWidth: 280 }} />
      </div>
      <ScrollTable heads={["Kupac","Telefon","Adresa","Porudžbine","Ukupno potrošeno","Poslednja porudžbina","Tier",""]}>
        {data.filter(c => {
          if (!search) return true;
          const q = search.toLowerCase();
          return [c.first, c.last, c.email, c.phone, c.address].some(v => (v || "").toLowerCase().includes(q));
        }).map((c, i) => {
          const ti = Math.min(i, 5);
          const first = c.first || "";
          const last = c.last || "";
          const displayName = [first, last].filter(Boolean).join(" ") || c.email || "—";
          const initials = (first[0] || "") + (last[0] || "") || "?";
          return (
            <tr key={c.id} onClick={() => onViewContact(c.id)} style={{ cursor: "pointer" }}>
              <TD>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 30, height: 30, borderRadius: "50%", background: tierBg[ti], color: tierFg[ti], display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 500, flexShrink: 0 }}>{initials}</div>
                  <span style={{ fontWeight: 500 }}>{displayName}</span>
                </div>
              </TD>
              <TD style={{ color: "#888", fontSize: 12 }}>{c.phone || "—"}</TD>
              <TD style={{ color: "#888", fontSize: 12 }}>{c.address || "—"}</TD>
              <TD>{c.orderCount}</TD>
              <TD style={{ fontWeight: 500 }}>{c.total.toLocaleString()} RSD</TD>
              <TD style={{ color: "#888" }}>{c.lastOrderDate}</TD>
              <TD><span style={{ background: tierBg[ti], color: tierFg[ti], padding: "3px 8px", borderRadius: 20, fontSize: 11, fontWeight: 500 }}>{tierLabel[ti]}</span></TD>
              <TD><Btn size="xs" onClick={e => { e.stopPropagation(); onViewContact(c.id); }}>Pregled</Btn></TD>
            </tr>
          );
        })}
      </ScrollTable>

      <Modal open={showContact} onClose={() => setShowContact(false)} title="Dodaj kontakt"
        footer={<><Btn onClick={() => setShowContact(false)}>Otkaži</Btn><Btn variant="primary" onClick={saveContact}>Sačuvaj</Btn></>}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
          <FormGroup label="Ime"><Input value={newContact.first} onChange={e => setNewContact({ ...newContact, first: e.target.value })} placeholder="Marko" /></FormGroup>
          <FormGroup label="Prezime"><Input value={newContact.last} onChange={e => setNewContact({ ...newContact, last: e.target.value })} placeholder="Petrović" /></FormGroup>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
          <FormGroup label="Telefon"><Input value={newContact.phone} onChange={e => setNewContact({ ...newContact, phone: e.target.value })} placeholder="+381 60 123 4567" /></FormGroup>
          <FormGroup label="Email"><Input value={newContact.email} onChange={e => setNewContact({ ...newContact, email: e.target.value })} placeholder="marko@email.com" /></FormGroup>
        </div>
        <FormGroup label="Adresa"><Input value={newContact.address} onChange={e => setNewContact({ ...newContact, address: e.target.value })} placeholder="Ulica, Grad, Poštanski broj" /></FormGroup>
        <FormGroup label="Napomena"><Input value={newContact.note} onChange={e => setNewContact({ ...newContact, note: e.target.value })} placeholder="Opciono" /></FormGroup>
      </Modal>
    </div>
  );
}

function CRMDetail({ contactId, contacts, setContacts, orders, onBack, onViewOrder }) {
  const c = contacts.find(x => x.id === contactId);
  const [showEdit, setShowEdit] = useState(false);
  const [editData, setEditData] = useState({});

  if (!c) return null;
  const co = [...orders.filter(o => (o.customerId === c.id || (c.email && o.email === c.email)) && o.status !== "cancelled")].sort((a, b) => b.date.localeCompare(a.date));
  const total = co.reduce((s, o) => s + o.total, 0);
  const initials = (c.first[0] || "") + (c.last[0] || "");

  function openEdit() {
    setEditData({ first: c.first || "", last: c.last || "", phone: c.phone || "", email: c.email || "", address: c.address || "", note: c.note || "" });
    setShowEdit(true);
  }

  function saveEdit() {
    setContacts(prev => prev.map(x => x.id === c.id ? { ...x, ...editData } : x));
    setShowEdit(false);
  }

  return (
    <div>
      <div onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", color: "#888", fontSize: 13, marginBottom: 16 }}>← Nazad na kupce</div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#FAEEDA", color: "#854F0B", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 500 }}>{initials}</div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 500 }}>{c.first} {c.last}</div>
            <div style={{ fontSize: 13, color: "#888" }}>{c.phone || ""}{c.email ? " · " + c.email : ""}</div>
            {c.address && <div style={{ fontSize: 12, color: "#aaa", marginTop: 2 }}>{c.address}</div>}
          </div>
        </div>
        <Btn size="sm" onClick={openEdit}>Izmeni kupca</Btn>
      </div>

      <Modal open={showEdit} onClose={() => setShowEdit(false)} title="Izmeni kupca"
        footer={<><Btn onClick={() => setShowEdit(false)}>Otkaži</Btn><Btn variant="primary" onClick={saveEdit}>Sačuvaj</Btn></>}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
          <FormGroup label="Ime"><Input value={editData.first || ""} onChange={e => setEditData(d => ({ ...d, first: e.target.value }))} /></FormGroup>
          <FormGroup label="Prezime"><Input value={editData.last || ""} onChange={e => setEditData(d => ({ ...d, last: e.target.value }))} /></FormGroup>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
          <FormGroup label="Telefon"><Input value={editData.phone || ""} onChange={e => setEditData(d => ({ ...d, phone: e.target.value }))} /></FormGroup>
          <FormGroup label="Email"><Input type="email" value={editData.email || ""} onChange={e => setEditData(d => ({ ...d, email: e.target.value }))} /></FormGroup>
        </div>
        <FormGroup label="Adresa" style={{ marginBottom: 10 }}><Input value={editData.address || ""} onChange={e => setEditData(d => ({ ...d, address: e.target.value }))} /></FormGroup>
        <FormGroup label="Napomena"><Input value={editData.note || ""} onChange={e => setEditData(d => ({ ...d, note: e.target.value }))} /></FormGroup>
      </Modal>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: 10, marginBottom: 20 }}>
        <MetricCard label="Ukupno porudžbina" value={co.length} />
        <MetricCard label="Ukupno potrošeno" value={total.toLocaleString() + " RSD"} color="#1D9E75" />
        <MetricCard label="Poslednja porudžbina" value={co.length ? co[0].date : "—"} />
      </div>
      <ScrollTable heads={["#","Datum","Tip","Ukupno","PDV","Status"]}>
        {co.map(o => (
          <tr key={o.id} style={{ cursor: "pointer" }} onClick={() => onViewOrder(o)}>
            <TD style={{ fontWeight: 500 }}>#{o.id}</TD>
            <TD>{o.date}</TD>
            <TD><Badge type={o.type} /></TD>
            <TD style={{ fontWeight: 500 }}>{o.total.toLocaleString()} RSD</TD>
            <TD style={{ color: "#534AB7" }}>{Math.round(pdvFromPrice(o.total)).toLocaleString()} RSD</TD>
            <TD><Badge type={o.status} /></TD>
          </tr>
        ))}
      </ScrollTable>
    </div>
  );
}

// ── Detaljan Pregled ──────────────────────────────────────────────────────────

function DetaljanPregled({ orders, prodCosts, purchasePrices, marketingEntries, fromDate, toDate, setFromDate, setToDate, shippingCfg, noShippingIds }) {

  const filtered = orders.filter(o =>
    o.date >= fromDate && o.date <= toDate &&
    o.status !== "cancelled" &&
    o.lines && o.lines.length > 0 &&
    o.type !== "print"
  );

  function exportDetaljan() {
    const rows = [];
    filtered.forEach(order => {
      const ordersOnDay = orders.filter(o => o.date === order.date && o.status !== "cancelled" && o.type === "web");
      const dailyMkt = marketingEntries.filter(e => e.date === order.date).reduce((s, e) => s + e.amount, 0);
      const promo = (order.type === "web" && ordersOnDay.length > 0) ? dailyMkt / ordersOnDay.length : 0;
      order.lines.forEach(l => {
        const pp = getPurchasePrice(l.product, l.color, purchasePrices, order.date);
        const costs = getApplicableCosts(l.product, prodCosts);
        const r = calcItemProfit(l.price, pp, costs);
        const totalQty = order.lines.reduce((s, x) => s + x.qty, 0);
        const promoPerItem = promo / Math.max(totalQty, 1);
        const netoWithPromo = r.netoDobit - promoPerItem;
        const pct = l.price > 0 ? (netoWithPromo / l.price) * 100 : 0;
        const prodCostVal = Math.round(totalProdCost(pp, costs) - pp);
        for (let i = 0; i < l.qty; i++) {
          rows.push({
            "Porudžbina":        `#${order.id}`,
            "Kupac":             order.customer || "",
            "Datum":             order.date,
            "Tip":               order.type === "web" ? "WooCommerce" : "Direktna",
            "Proizvod":          l.product,
            "Boja":              NO_VARIANTS.includes(l.product) ? "—" : (l.color || "—"),
            "Veličina":          NO_VARIANTS.includes(l.product) ? "—" : (l.size || "—"),
            "Dizajn":            l.dizajn || "—",
            "Prod. cena (RSD)":  l.price,
            "Nab. cena (RSD)":   Math.round(pp),
            "Proiz. troš. (RSD)": prodCostVal,
            "UPC (RSD)":         Math.round(r.upc),
            "Dobit+PDV (RSD)":   Math.round(r.dobitPdv),
            "PDV (RSD)":         Math.round(r.pdv),
            "Promo (RSD)":       Math.round(promoPerItem),
            "Neto dobit (RSD)":  Math.round(netoWithPromo),
            "Neto+PDV (RSD)":    Math.round(netoWithPromo + r.pdv),
            "% Zarade":          parseFloat(pct.toFixed(1)),
          });
        }
      });
    });
    downloadXLSX(
      [["Detaljan pregled", buildSheet(rows, [14, 22, 12, 12, 18, 10, 10, 20, 16, 14, 16, 12, 14, 10, 12, 14, 12, 10])]],
      `DetaljanPregled_${fromDate}_${toDate}.xlsx`
    );
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <div style={{ fontSize: 18, fontWeight: 500 }}>Detaljan pregled</div>
        {filtered.length > 0 && <Btn size="sm" onClick={exportDetaljan}>⬇ Preuzmi Excel</Btn>}
      </div>
      <div style={{ fontSize: 13, color: "#888", marginBottom: 16 }}>Pregled troškova i dobiti po stavci, grupisano po porudžbini.</div>
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <label style={{ fontSize: 12, color: "#888" }}>Od</label>
        <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={{ width: 150 }} />
        <label style={{ fontSize: 12, color: "#888" }}>Do</label>
        <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={{ width: 150 }} />
        <Btn size="sm">Primeni</Btn>
      </div>
      {filtered.length === 0 && <div style={{ color: "#aaa", fontSize: 13, padding: 20, textAlign: "center" }}>Nema podataka za izabrani period.</div>}
      {filtered.map(order => {
        const ordersOnDay = orders.filter(o => o.date === order.date && o.status !== "cancelled" && o.type === "web");
        const dailyMkt = marketingEntries.filter(e => e.date === order.date).reduce((s, e) => s + e.amount, 0);
        const promo = (order.type === "web" && ordersOnDay.length > 0) ? dailyMkt / ordersOnDay.length : 0;
        return (
          <SectionCard key={order.id} title={null}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontWeight: 500, fontSize: 15 }}>#{order.id} — {order.customer}</span>
                <Badge type={order.type} /><Badge type={order.status} />
                {order.discount > 0 && <span style={{ fontSize: 12, color: "#E24B4A", background: "#FEE8E7", borderRadius: 4, padding: "2px 7px" }}>Popust {order.discount}% (−{Math.round(order.lines.reduce((s, l) => s + l.price * l.qty, 0) * (order.discount / 100)).toLocaleString()} RSD)</span>}
              </div>
              <span style={{ fontSize: 12, color: "#888" }}>{order.date}</span>
            </div>
            {(() => { const thr = order.shippingThreshold ?? shippingCfg?.freeShippingThreshold ?? FREE_SHIPPING_THRESHOLD; const sc = shippingCfg?.shippingCost ?? SHIPPING_COST; const isSam = noShippingIds?.has(String(order.id)); return !isSam && (order.shipping > 0 || order.total >= thr) && (
              <div style={{ fontSize: 12, color: "#888", marginBottom: 10 }}>
                {order.total >= thr
                  ? <span>Dostava: <strong>{sc.toLocaleString()} RSD</strong> <span style={{ color: "#F4A261" }}>(plaća Mashina — oduzeto od neto dobiti)</span></span>
                  : <span>Dostava: <strong>{order.shipping.toLocaleString()} RSD</strong> <span style={{ color: "#888" }}>(plaća kupac)</span></span>
                }
              </div>
            ); })()}
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 900 }}>
                <thead>
                  <tr>
                    {["Proizvod","Boja","Vel.","Dizajn","Prod. cena","Nab. cena","Proiz. troš.","UPC","Dobit+PDV","PDV","Promo","Neto Dobit","Neto+PDV","% Zarade"].map(h => (
                      <th key={h} style={{ textAlign: "left", padding: "6px 8px", fontSize: 10, color: "#888", borderBottom: "0.5px solid #eee", textTransform: "uppercase", letterSpacing: 0.3, fontWeight: 500, whiteSpace: "nowrap", background: "#fafafa" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {order.lines.map((l, li) => {
                    const pp = getPurchasePrice(l.product, l.color, purchasePrices, order.date);
                    const applicableCosts = SIMPLE_PRODUCTS.includes(l.product) ? {} : PRODUCT_COST_KEYS[l.product] ? Object.fromEntries(Object.entries(prodCosts).filter(([k]) => PRODUCT_COST_KEYS[l.product].includes(k))) : prodCosts;
      const r = calcItemProfit(l.price, pp, applicableCosts);
                    const totalQty = order.lines.reduce((s, x) => s + x.qty, 0);
                    const promoPerItem = promo / Math.max(totalQty, 1);
                    const netoWithPromo = r.netoDobit - promoPerItem;
                    const pctWithPromo = l.price > 0 ? (netoWithPromo / l.price) * 100 : 0;
                    return Array.from({ length: l.qty }, (_, ii) => (
                      <tr key={`${li}-${ii}`} style={{ background: ii % 2 === 1 ? "#fafafa" : "transparent" }}>
                        <td style={{ padding: "7px 8px", borderBottom: "0.5px solid #eee", fontWeight: 500 }}>{l.product}</td>
                        <td style={{ padding: "7px 8px", borderBottom: "0.5px solid #eee", color: "#888" }}>{NO_VARIANTS.includes(l.product) ? "—" : l.color}</td>
                        <td style={{ padding: "7px 8px", borderBottom: "0.5px solid #eee", color: "#888" }}>{NO_VARIANTS.includes(l.product) ? "—" : l.size}</td>
                        <td style={{ padding: "7px 8px", borderBottom: "0.5px solid #eee", color: "#888", maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis" }}>{l.dizajn || "—"}</td>
                        <td style={{ padding: "7px 8px", borderBottom: "0.5px solid #eee", fontWeight: 500 }}>{l.price.toLocaleString()}</td>
                        <td style={{ padding: "7px 8px", borderBottom: "0.5px solid #eee", color: "#888" }}>{pp.toLocaleString()}</td>
                        <td style={{ padding: "7px 8px", borderBottom: "0.5px solid #eee", color: "#888" }}>{Math.round(totalProdCost(pp, getApplicableCosts(l.product, prodCosts)) - pp).toLocaleString()}</td>
                        <td style={{ padding: "7px 8px", borderBottom: "0.5px solid #eee", color: "#888" }}>{Math.round(r.upc).toLocaleString()}</td>
                        <td style={{ padding: "7px 8px", borderBottom: "0.5px solid #eee" }}>{Math.round(r.dobitPdv).toLocaleString()}</td>
                        <td style={{ padding: "7px 8px", borderBottom: "0.5px solid #eee", color: "#534AB7" }}>{Math.round(r.pdv).toLocaleString()}</td>
                        <td style={{ padding: "7px 8px", borderBottom: "0.5px solid #eee", color: "#854F0B" }}>{Math.round(promoPerItem).toLocaleString()}</td>
                        <td style={{ padding: "7px 8px", borderBottom: "0.5px solid #eee", fontWeight: 500, color: netoWithPromo >= 0 ? "#1D9E75" : "#E24B4A" }}>{Math.round(netoWithPromo).toLocaleString()}</td>
                        <td style={{ padding: "7px 8px", borderBottom: "0.5px solid #eee", fontWeight: 500, color: (netoWithPromo + r.pdv) >= 0 ? "#1D9E75" : "#E24B4A" }}>{Math.round(netoWithPromo + r.pdv).toLocaleString()}</td>
                        <td style={{ padding: "7px 8px", borderBottom: "0.5px solid #eee", fontWeight: 500, color: pctWithPromo >= 0 ? "#1D9E75" : "#E24B4A" }}>{pctWithPromo.toFixed(1)}%</td>
                      </tr>
                    ));
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ background: "#f5f5f5" }}>
                    {(() => {
                      const p = calcOrderProfit(order, prodCosts, purchasePrices, marketingEntries, orders, { ...shippingCfg, noShipping: noShippingIds?.has(String(order.id)) });
                      return (
                        <>
                          <td colSpan={4} style={{ padding: "8px", fontSize: 12, fontWeight: 500, color: "#888" }}>UKUPNO</td>
                          <td style={{ padding: "8px", fontWeight: 500 }}>{order.total.toLocaleString()}</td>
                          <td colSpan={6} style={{ padding: "8px" }}></td>
                          <td style={{ padding: "8px", fontWeight: 500, color: p.netoDobit >= 0 ? "#1D9E75" : "#E24B4A" }}>{Math.round(p.netoDobit).toLocaleString()}</td>
                          <td style={{ padding: "8px", fontWeight: 500, color: (p.netoDobit + p.pdv) >= 0 ? "#1D9E75" : "#E24B4A" }}>{Math.round(p.netoDobit + p.pdv).toLocaleString()}</td>
                          <td style={{ padding: "8px", fontWeight: 500, color: p.pctZarade >= 0 ? "#1D9E75" : "#E24B4A" }}>{p.pctZarade.toFixed(1)}%</td>
                        </>
                      );
                    })()}
                  </tr>
                </tfoot>
              </table>
            </div>
          </SectionCard>
        );
      })}
    </div>
  );
}

// ── Troškovi Proizvodnje ──────────────────────────────────────────────────────

function TroskoviProizvodnje({ prodCosts, setProdCosts, purchasePrices, setPurchasePrices }) {
  const [form, setForm] = useState({ ...prodCosts });
  const [saved, setSaved] = useState(false);
  const [ppSaved, setPpSaved] = useState(false);

  const flattenPP = (pp) => {
    const flat = {};
    Object.entries(pp).forEach(([prod, prices]) => {
      Object.entries(prices).forEach(([key, val]) => {
        const price = Array.isArray(val) ? val[val.length - 1].price : val;
        flat[`${prod}|||${key}`] = String(price);
      });
    });
    return flat;
  };
  const [ppForm, setPpForm] = useState(() => flattenPP(purchasePrices));

  function save() {
    const parsed = {};
    Object.keys(form).forEach(k => { parsed[k] = parseFloat(form[k]) || 0; });
    setProdCosts(parsed);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  function savePP() {
    const todayStr = today();
    const nested = {};
    Object.entries(ppForm).forEach(([key, val]) => {
      const [prod, colorKey] = key.split("|||");
      if (!nested[prod]) nested[prod] = {};
      const existing = purchasePrices[prod]?.[colorKey];
      const currentEntries = Array.isArray(existing) ? existing
        : typeof existing === "number" ? [{ from: "2000-01-01", price: existing }]
        : [];
      const newPrice = parseFloat(val) || 0;
      const lastPrice = currentEntries.length > 0 ? currentEntries[currentEntries.length - 1].price : null;
      nested[prod][colorKey] = newPrice !== lastPrice
        ? [...currentEntries, { from: todayStr, price: newPrice }]
        : currentEntries;
    });
    setPurchasePrices(nested);
    setPpSaved(true);
    setTimeout(() => setPpSaved(false), 2000);
  }

  const colorKeyLabel = (key) => key === "_default" ? "Ostale boje" : key;

  const total = Object.values(form).reduce((s, v) => s + (parseFloat(v) || 0), 0);

  return (
    <div>
      <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 6 }}>Troškovi proizvodnje</div>
      <div style={{ fontSize: 13, color: "#888", marginBottom: 4 }}>Fiksni troškovi po komadu koji se dodaju na nabavnu cenu pri obračunu dobiti.</div>
      <div style={{ fontSize: 12, color: "#1D9E75", marginBottom: 20, background: "#EAF3DE", padding: "8px 12px", borderRadius: 8 }}>
        ✓ Izmene važe samo za buduće porudžbine — završene porudžbine čuvaju originalne cene.
      </div>
      <SectionCard title="Troškovi po komadu (RSD)">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 12, marginBottom: 16 }}>
          {Object.entries(form).map(([key, val]) => (
            <FormGroup key={key} label={key}>
              <Input type="number" min="0" step="1" value={val} onChange={e => setForm({ ...form, [key]: e.target.value })} />
            </FormGroup>
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderTop: "0.5px solid #eee", paddingTop: 12 }}>
          <div style={{ fontSize: 13 }}>
            <span style={{ color: "#888" }}>Ukupni fiksni troškovi po komadu: </span>
            <span style={{ fontWeight: 500 }}>{total.toLocaleString()} RSD</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {saved && <span style={{ fontSize: 12, color: "#1D9E75" }}>✓ Sačuvano</span>}
            <Btn variant="primary" onClick={save}>Sačuvaj izmene</Btn>
          </div>
        </div>
      </SectionCard>
      <SectionCard title="Nabavne cene po proizvodu (RSD)">
        <div style={{ fontSize: 12, color: "#1D9E75", marginBottom: 14, background: "#EAF3DE", padding: "8px 12px", borderRadius: 8 }}>
          ✓ Izmene važe samo za buduće obračune — prethodni obračuni nisu izmenjeni.
        </div>
        {Object.entries(purchasePrices).map(([prod, prices]) => (
          <div key={prod} style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 500, color: "#888", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.3 }}>{prod}</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 10 }}>
              {Object.entries(prices).map(([colorKey]) => {
                const flatKey = `${prod}|||${colorKey}`;
                return (
                  <FormGroup key={flatKey} label={colorKeyLabel(colorKey)}>
                    <Input type="number" min="0" step="1" value={ppForm[flatKey] ?? ""} onChange={e => setPpForm({ ...ppForm, [flatKey]: e.target.value })} />
                  </FormGroup>
                );
              })}
            </div>
          </div>
        ))}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10, borderTop: "0.5px solid #eee", paddingTop: 12 }}>
          {ppSaved && <span style={{ fontSize: 12, color: "#1D9E75" }}>✓ Sačuvano</span>}
          <Btn variant="primary" onClick={savePP}>Sačuvaj nabavne cene</Btn>
        </div>
      </SectionCard>

      <SectionCard title="Pregled po proizvodu">
        <ScrollTable heads={["Proizvod","Prod. cena (default)","Nab. cena (primer)","Fiksni troškovi","UPC","PDV (20%)","Break-even"]}>
          {Object.entries(DEFAULT_SELLING_PRICES).map(([product]) => {
            const sellPrice = DEFAULT_SELLING_PRICES[product];
            const pp = getPurchasePrice(product, "Bela", purchasePrices, today());
            const upc = totalProdCost(pp, form);
            const pdv = pdvFromPrice(sellPrice);
            return (
              <tr key={product}>
                <TD style={{ fontWeight: 500 }}>{product}</TD>
                <TD style={{ color: "#1D9E75", fontWeight: 500 }}>{sellPrice.toLocaleString()} RSD</TD>
                <TD>{pp.toLocaleString()} RSD</TD>
                <TD>{total.toLocaleString()} RSD</TD>
                <TD style={{ fontWeight: 500 }}>{Math.round(upc).toLocaleString()} RSD</TD>
                <TD style={{ color: "#534AB7" }}>{Math.round(pdv).toLocaleString()} RSD</TD>
                <TD style={{ color: "#E24B4A", fontWeight: 500 }}>{Math.round(upc + pdv).toLocaleString()} RSD</TD>
              </tr>
            );
          })}
        </ScrollTable>
      </SectionCard>

    </div>
  );
}

// ── Za slanje ─────────────────────────────────────────────────────────────────

function ZaSlanje({ orders, setOrders, contacts, sentDates, setSentDates, zoneOverrides, setZoneOverrides }) {
  const [tab, setTab] = useState("beograd");

  function isSamostalno(o) { return zoneOverrides[o.id] === "samostalno"; }

  function isBeograd(o) {
    if (zoneOverrides[o.id] === "beograd") return true;
    if (zoneOverrides[o.id] === "srbija" || zoneOverrides[o.id] === "samostalno") return false;
    const bgTerms = s => s.includes("beograd") || s.includes("belgrade") || s.includes("zemun");
    if (bgTerms((o.city || "").toLowerCase())) return true;
    if (bgTerms((o.address || "").toLowerCase())) return true;
    const contact = contacts.find(c => c.id && c.id === o.customerId) ||
                    contacts.find(c => c.email && c.email === o.email);
    const cAddr = contact ? (contact.address || "").toLowerCase() : "";
    return bgTerms(cAddr);
  }

  function moveZone(o, zone) {
    setZoneOverrides(prev => ({ ...prev, [o.id]: zone }));
    pushZoneOverride(o.id, zone);
  }

  const printed = orders.filter(o => o.status === "odštampano");
  const isporuceno = orders.filter(o => o.status === "poslato");
  const bgOrders = printed.filter(o => !isSamostalno(o) && isBeograd(o));
  const srbijaOrders = printed.filter(o => !isSamostalno(o) && !isBeograd(o));
  const samostalnoOrders = printed.filter(o => isSamostalno(o));
  const isporucenoBg = isporuceno.filter(o => !isSamostalno(o) && isBeograd(o));
  const isporucenaSrbija = isporuceno.filter(o => !isSamostalno(o) && !isBeograd(o));
  const isporucenaSamostalno = isporuceno.filter(o => isSamostalno(o));
  const isIsporuceno = tab === "isporuceno_bg" || tab === "isporuceno_srbija" || tab === "isporuceno_samostalno";

  const shown = tab === "beograd" ? bgOrders
    : tab === "srbija" ? srbijaOrders
    : tab === "samostalno" ? samostalnoOrders
    : tab === "isporuceno_bg" ? isporucenoBg
    : tab === "isporuceno_srbija" ? isporucenaSrbija
    : isporucenaSamostalno;

  function groupByDate(list) {
    const map = {};
    list.forEach(o => {
      const key = sentDates[o.id] || "nepoznato";
      if (!map[key]) map[key] = [];
      map[key].push(o);
    });
    return Object.entries(map).sort(([a], [b]) => {
      if (a === "nepoznato") return 1;
      if (b === "nepoznato") return -1;
      return b.localeCompare(a);
    });
  }

  function markIsporuceno(o) {
    const date = today();
    const upd = { ...o, status: "poslato" };
    setOrders(prev => prev.map(x => x.id === o.id ? upd : x));
    setSentDates(prev => ({ ...prev, [o.id]: date }));
    pushSentDate(o.id, date);
    if (o.type !== "web") pushDirectOrder(upd); else pushStatusToServer(o.id, "poslato");
  }

  function cancelIsporuceno(o) {
    const upd = { ...o, status: "odštampano" };
    setOrders(prev => prev.map(x => x.id === o.id ? upd : x));
    if (o.type !== "web") pushDirectOrder(upd); else pushStatusToServer(o.id, "odštampano");
  }

  function OrderCard({ o, actions }) {
    const contact = contacts.find(c => c.email && c.email === o.email);
    const address = o.address || (contact ? contact.address : "") || "—";
    const phone = o.phone || (contact ? contact.phone : "") || "—";
    const hasFreeShipping = o.total >= (o.shippingThreshold ?? FREE_SHIPPING_THRESHOLD);
    return (
      <SectionCard key={o.id}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
          <div>
            <div style={{ fontWeight: 500, fontSize: 14 }}>#{o.id} — {o.customer}</div>
            <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>Poručeno: {o.date}</div>
            {isIsporuceno && <div style={{ fontSize: 12, color: "#888", marginTop: 1 }}>Datum isporuke: {fmtDate(sentDates[o.id])}</div>}
          </div>
          <div style={{ display: "flex", gap: 6 }}><Badge type={o.type} /><Badge type={o.status} /></div>
        </div>
        <div style={{ fontSize: 13, marginBottom: 4 }}>
          <span style={{ color: "#888", marginRight: 6 }}>Adresa:</span>{address}
        </div>
        <div style={{ fontSize: 13, marginBottom: 8 }}>
          <span style={{ color: "#888", marginRight: 6 }}>Telefon:</span>{phone}
        </div>
        <div style={{ fontSize: 12, color: "#555", borderTop: "0.5px solid #eee", paddingTop: 8, marginBottom: 6 }}>
          {o.lines.map((l, i) => (
            <div key={i}>{l.qty}× {l.product}{!NO_VARIANTS.includes(l.product) ? ` · ${l.color} · ${l.size}` : ""}{l.dizajn ? ` · ${l.dizajn}` : ""}</div>
          ))}
        </div>
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: hasFreeShipping ? 6 : 10 }}>
          <span style={{ color: "#888", marginRight: 6, fontWeight: 400 }}>Ukupno:</span>{o.total.toLocaleString()} RSD
        </div>
        {hasFreeShipping && (
          <div style={{ fontSize: 12, color: "#1D9E75", fontWeight: 500, marginBottom: 10 }}>Besplatna dostava</div>
        )}
        <div style={{ display: "flex", gap: 6 }}>{actions}</div>
      </SectionCard>
    );
  }

  return (
    <div>
      <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 6 }}>Isporuka</div>
      <div style={{ display: "flex", gap: 4, marginBottom: 16, background: "#f5f5f5", borderRadius: 8, padding: 4, width: "fit-content", flexWrap: "wrap" }}>
        {[
          ["beograd", `Beograd (${bgOrders.length})`],
          ["srbija", `Srbija (${srbijaOrders.length})`],
          ["samostalno", `Samostalno (${samostalnoOrders.length})`],
          ["isporuceno_bg", `Isporučeno BG (${isporucenoBg.length})`],
          ["isporuceno_srbija", `Isporučeno Srbija (${isporucenaSrbija.length})`],
          ["isporuceno_samostalno", `Isporučeno Sam. (${isporucenaSamostalno.length})`],
        ].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={{ padding: "5px 14px", borderRadius: 6, border: "none", background: tab === k ? "#fff" : "transparent", fontWeight: tab === k ? 500 : 400, cursor: "pointer", fontSize: 13, fontFamily: "inherit", boxShadow: tab === k ? "0 0 0 0.5px #ddd" : "none" }}>{l}</button>
        ))}
      </div>
      {isIsporuceno ? (
        shown.length === 0
          ? <div style={{ color: "#aaa", fontSize: 13, padding: 20, textAlign: "center" }}>Nema porudžbina.</div>
          : groupByDate(shown).map(([date, group]) => {
              const dayTotal = group.reduce((s, o) => s + o.total, 0);
              return (
                <div key={date}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 4px", marginBottom: 6, borderBottom: "1px solid #eee", fontSize: 12, color: "#888" }}>
                    <span style={{ fontWeight: 500, color: date === "nepoznato" ? "#bbb" : "#555" }}>
                      {date === "nepoznato" ? "nepoznato" : fmtDate(date)}
                    </span>
                    <span>{group.length} · {dayTotal.toLocaleString()} RSD</span>
                  </div>
                  {group.map(o => (
                    <OrderCard key={o.id} o={o} actions={[
                      <Btn key="cancel" size="sm" onClick={() => cancelIsporuceno(o)}>Poništi isporuku</Btn>
                    ]} />
                  ))}
                </div>
              );
            })
      ) : (
        <>
          {shown.length === 0 && <div style={{ color: "#aaa", fontSize: 13, padding: 20, textAlign: "center" }}>Nema porudžbina.</div>}
          {shown.map(o => {
            const moveButtons = tab === "beograd"
              ? [<Btn key="ms" size="sm" onClick={() => moveZone(o, "srbija")}>→ Srbija</Btn>, <Btn key="mm" size="sm" onClick={() => moveZone(o, "samostalno")}>→ Samostalno</Btn>]
              : tab === "srbija"
              ? [<Btn key="mb" size="sm" onClick={() => moveZone(o, "beograd")}>→ Beograd</Btn>, <Btn key="mm" size="sm" onClick={() => moveZone(o, "samostalno")}>→ Samostalno</Btn>]
              : [<Btn key="mb" size="sm" onClick={() => moveZone(o, "beograd")}>→ Beograd</Btn>, <Btn key="ms" size="sm" onClick={() => moveZone(o, "srbija")}>→ Srbija</Btn>];
            return (
              <OrderCard key={o.id} o={o} actions={[
                ...moveButtons,
                <Btn key="send" size="sm" variant="primary" onClick={() => markIsporuceno(o)}>Označi kao Isporučeno</Btn>
              ]} />
            );
          })}
        </>
      )}
    </div>
  );
}

// ── Auth ──────────────────────────────────────────────────────────────────────

// ── Podela zarade ─────────────────────────────────────────────────────────────

function PodelaZarade({ orders, expenses, marketingEntries, prodCosts, purchasePrices, shippingCfg, noShippingIds, fromDate, toDate, setFromDate, setToDate, config, setConfig }) {
  const fOrders = orders.filter(o => o.date >= fromDate && o.date <= toDate && o.status !== "cancelled");
  const fMkt = marketingEntries.filter(e => e.date >= fromDate && e.date <= toDate);
  const fExp = expenses.filter(e => e.date >= fromDate && e.date <= toDate);
  const totalExp = fExp.reduce((s, e) => s + e.amount, 0);
  const profitData = fOrders.map(o => calcOrderProfit(o, prodCosts, purchasePrices, marketingEntries, orders, { ...shippingCfg, noShipping: noShippingIds?.has(String(o.id)) }));
  const daysWithWebOrders = new Set(fOrders.filter(o => o.type === "web").map(o => o.date));
  const unallocatedMkt = fMkt.reduce((s, e) => daysWithWebOrders.has(e.date) ? s : s + e.amount, 0);
  const totalNeto = profitData.reduce((s, p) => s + p.netoDobit, 0) - unallocatedMkt - totalExp;
  const totalPdv = profitData.reduce((s, p) => s + p.pdv, 0);
  const base = totalNeto + totalPdv;
  const itemsSold = fOrders.reduce((s, o) => s + (o.lines || []).reduce((ls, l) => ls + (l.qty || 0), 0), 0);

  const [baseOverride, setBaseOverride] = useState(null);
  const activeBase = baseOverride !== null ? baseOverride : Math.round(base);

  const periodDays = Math.max(1, (new Date(toDate) - new Date(fromDate)) / 86400000 + 1);
  const periodMonths = periodDays / 30;

  const { participants } = config;
  const totalSalaries = participants.reduce((s, p) => s + (p.salary || 0) * periodMonths, 0);
  const totalRoyalties = participants.reduce((s, p) => s + (p.royaltyPerItem || 0) * itemsSold, 0);
  const distributable = activeBase - totalSalaries - totalRoyalties;
  const totalPct = participants.reduce((s, p) => s + (p.profitSharePct || 0), 0);

  function redistributePct(parts, changedId, newPct) {
    const locked = parts.filter(p => p.id !== changedId && p.locked);
    const unlocked = parts.filter(p => p.id !== changedId && !p.locked);
    const lockedTotal = locked.reduce((s, p) => s + (p.profitSharePct || 0), 0);
    const remaining = 100 - lockedTotal - newPct;
    const perUnlocked = unlocked.length > 0 ? Math.max(0, remaining / unlocked.length) : 0;
    return parts.map(p => {
      if (p.id === changedId) return { ...p, profitSharePct: newPct };
      if (p.locked) return p;
      return { ...p, profitSharePct: Math.round(perUnlocked * 10) / 10 };
    });
  }

  function update(id, field, value) {
    if (field === "profitSharePct") {
      const v = Math.max(0, Math.min(100, parseFloat(value) || 0));
      setConfig(prev => ({ ...prev, participants: redistributePct(prev.participants, id, v) }));
    } else {
      setConfig(prev => ({ ...prev, participants: prev.participants.map(p => p.id === id ? { ...p, [field]: value } : p) }));
    }
  }

  function toggleLock(id) {
    setConfig(prev => ({ ...prev, participants: prev.participants.map(p => p.id === id ? { ...p, locked: !p.locked } : p) }));
  }

  function addParticipant() {
    setConfig(prev => ({ ...prev, participants: [...prev.participants, { id: Date.now(), name: "Novi učesnik", salary: 0, royaltyPerItem: 0, profitSharePct: 0, locked: false }] }));
  }

  function removeParticipant(id) {
    setConfig(prev => ({ ...prev, participants: prev.participants.filter(p => p.id !== id) }));
  }

  const pctOk = Math.abs(totalPct - 100) < 0.1 || participants.every(p => p.profitSharePct === 0);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ fontSize: 18, fontWeight: 500 }}>Podela zarade</div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} />
          <span style={{ color: "#aaa" }}>—</span>
          <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} />
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10, marginBottom: 20 }}>
        <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 10, padding: "12px 14px" }}>
          <div style={{ fontSize: 11, color: "#888", marginBottom: 6, fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.4 }}>Neto + PDV</div>
          <input
            type="number"
            value={activeBase}
            onChange={e => setBaseOverride(parseFloat(e.target.value) || 0)}
            style={{ width: "100%", border: "none", outline: "none", fontSize: 18, fontWeight: 600, color: "#85B7EB", background: "transparent", padding: 0 }}
          />
          {baseOverride !== null && (
            <div onClick={() => setBaseOverride(null)} style={{ fontSize: 11, color: "#888", cursor: "pointer", marginTop: 4 }}>↺ Povuci sa dashborda</div>
          )}
        </div>
        <MetricCard label="Plate" value={Math.round(totalSalaries).toLocaleString() + " RSD"} />
        <MetricCard label="Royalties" value={Math.round(totalRoyalties).toLocaleString() + " RSD"} />
        <MetricCard label="Za podelu" value={Math.round(distributable).toLocaleString() + " RSD"} color={distributable >= 0 ? "#1D9E75" : "#E24B4A"} />
        <MetricCard label="Stavki prodato" value={itemsSold.toString()} />
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div style={{ fontWeight: 500 }}>Učesnici</div>
        <Btn size="sm" onClick={addParticipant}>+ Dodaj učesnika</Btn>
      </div>
      {!pctOk && <div style={{ color: "#E24B4A", fontSize: 12, marginBottom: 8 }}>Zbir udela u dobiti mora biti 100% (trenutno: {totalPct}%)</div>}

      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
        {participants.map(p => {
          const salaryEarn = (p.salary || 0) * periodMonths;
          const royaltyEarn = (p.royaltyPerItem || 0) * itemsSold;
          const profitEarn = distributable * (p.profitSharePct || 0) / 100;
          const total = salaryEarn + royaltyEarn + profitEarn;
          return (
            <SectionCard key={p.id}>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
                <FormGroup label="Ime" style={{ flex: "1 1 90px" }}>
                  <Input value={p.name} onChange={e => update(p.id, "name", e.target.value)} />
                </FormGroup>
                <FormGroup label="Plata/mesec (RSD)" style={{ flex: "1 1 90px" }}>
                  <Input type="number" value={p.salary || ""} placeholder="0" onChange={e => update(p.id, "salary", parseFloat(e.target.value) || 0)} />
                </FormGroup>
                <FormGroup label="Po komadu (RSD)" style={{ flex: "1 1 90px" }}>
                  <Input type="number" value={p.royaltyPerItem || ""} placeholder="0" onChange={e => update(p.id, "royaltyPerItem", parseFloat(e.target.value) || 0)} />
                </FormGroup>
                <FormGroup label="Udeo u dobiti %" style={{ flex: "1 1 110px" }}>
                  <div style={{ display: "flex", gap: 4 }}>
                    <Input type="number" value={p.profitSharePct ?? ""} placeholder="0" min="0" max="100"
                      onChange={e => update(p.id, "profitSharePct", e.target.value)}
                      style={{ flex: 1 }} />
                    <button onClick={() => toggleLock(p.id)}
                      title={p.locked ? "Otključaj" : "Zaključaj"}
                      style={{ padding: "0 8px", border: "1px solid #ddd", borderRadius: 6, background: p.locked ? "#f0f0f0" : "#fff", cursor: "pointer", fontSize: 13 }}>
                      {p.locked ? "🔒" : "🔓"}
                    </button>
                  </div>
                </FormGroup>
                <div style={{ display: "flex", alignItems: "flex-end", gap: 8, paddingBottom: 2 }}>
                  <div style={{ fontWeight: 600, fontSize: 15, color: "#1D9E75", whiteSpace: "nowrap" }}>
                    {Math.round(total).toLocaleString()} RSD
                  </div>
                  <Btn size="xs" onClick={() => removeParticipant(p.id)} style={{ color: "#E24B4A" }}>×</Btn>
                </div>
              </div>
            </SectionCard>
          );
        })}
      </div>

      <div style={{ fontWeight: 500, marginBottom: 10 }}>Obračun</div>
      <div style={{ fontSize: 12, color: "#888", marginBottom: 8 }}>Period: {periodDays} dana ({periodMonths.toFixed(1)} meseci)</div>
      <ScrollTable heads={["Učesnik", "Plata za period", "Po komadu", "Udeo u dobiti", "Ukupno"]}>
        {participants.map(p => {
          const salaryEarn = (p.salary || 0) * periodMonths;
          const royaltyEarn = (p.royaltyPerItem || 0) * itemsSold;
          const profitEarn = distributable * (p.profitSharePct || 0) / 100;
          const total = salaryEarn + royaltyEarn + profitEarn;
          return (
            <tr key={p.id}>
              <TD style={{ fontWeight: 500 }}>{p.name}</TD>
              <TD>{Math.round(salaryEarn).toLocaleString()} RSD</TD>
              <TD>{Math.round(royaltyEarn).toLocaleString()} RSD</TD>
              <TD>{Math.round(profitEarn).toLocaleString()} RSD</TD>
              <TD style={{ fontWeight: 600, color: "#1D9E75" }}>{Math.round(total).toLocaleString()} RSD</TD>
            </tr>
          );
        })}
      </ScrollTable>
    </div>
  );
}

const AUTH_KEY = "mashina_token";
const AUTH_ROLE_KEY = "mashina_role";
const AUTH_USER_KEY = "mashina_user";
export function getAuthToken() { return localStorage.getItem(AUTH_KEY); }
export function getAuthRole() { return localStorage.getItem(AUTH_ROLE_KEY) || "user"; }
export function getAuthUsername() { return localStorage.getItem(AUTH_USER_KEY) || ""; }
function setAuthToken(t, role, username) { localStorage.setItem(AUTH_KEY, t); localStorage.setItem(AUTH_ROLE_KEY, role || "user"); if (username) localStorage.setItem(AUTH_USER_KEY, username); }
function clearAuthToken() { localStorage.removeItem(AUTH_KEY); localStorage.removeItem(AUTH_ROLE_KEY); localStorage.removeItem(AUTH_USER_KEY); }

function logActivity(action, type, entityId, details = "") {
  apiFetch(`${API_BASE}/api/activity-log`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action, type, entityId, details }),
  }).catch(() => {});
}

export async function apiFetch(url, options = {}) {
  const res = await fetch(url, {
    ...options,
    headers: { ...(options.headers || {}), "x-auth-token": getAuthToken() || "" },
  });
  if (res.status === 401) { clearAuthToken(); window.location.reload(); }
  return res;
}

function LoginScreen({ onLogin }) {
  const [form, setForm] = useState({ username: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API_BASE}/api/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) { setAuthToken(data.token, data.role, data.username); onLogin(); }
      else setError(data.error || "Greška pri prijavi.");
    } catch { setError("Ne mogu da se povežem sa serverom."); }
    setLoading(false);
  }

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f7f7f7" }}>
      <div style={{ background: "#fff", borderRadius: 12, padding: 40, width: 340, boxShadow: "0 2px 16px rgba(0,0,0,0.08)" }}>
        <div style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>{brand.appName}</div>
        <div style={{ fontSize: 13, color: "#888", marginBottom: 28 }}>Prijavite se na dashboard</div>
        <form onSubmit={handleSubmit}>
          <FormGroup label="Korisničko ime">
            <Input value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} placeholder="admin" autoFocus />
          </FormGroup>
          <FormGroup label="Lozinka">
            <Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="••••••••" />
          </FormGroup>
          {error && <div style={{ color: "#E24B4A", fontSize: 12, marginBottom: 12 }}>{error}</div>}
          <Btn variant="primary" style={{ width: "100%", marginTop: 8 }} disabled={loading}>
            {loading ? "Prijavljivanje…" : "Prijavi se"}
          </Btn>
        </form>
      </div>
    </div>
  );
}

// ── Bulk mark old orders as poslato ──────────────────────────────────────────
function BulkMarkPoslato() {
  const [cutoff, setCutoff] = useState("2026-03-01");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  async function handleRun() {
    if (!cutoff) return;
    if (!window.confirm(`Označi sve porudžbine pre ${cutoff} u statusu "U obradi" kao Poslato?`)) return;
    setLoading(true); setStatus(null);
    try {
      const res = await apiFetch(`${API_BASE}/api/bulk-mark-poslato`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cutoff }),
      });
      const data = await res.json();
      if (data.success) setStatus(`Završeno. Datum: ${data.cutoff}. Ažurirano WC porudžbina: ${data.wcUpdated}.`);
      else setStatus("Greška: " + (data.error || "nepoznata"));
    } catch { setStatus("Greška pri izvršavanju."); }
    setLoading(false);
  }

  return (
    <div style={{ background: "#fff", borderRadius: 10, border: "0.5px solid #eee", padding: 20, marginBottom: 28, color: "#111" }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: "#444" }}>Administratorske akcije</div>
      <div style={{ fontSize: 12, color: "#888", marginBottom: 12 }}>Označi sve porudžbine pre izabranog datuma u statusu "U obradi" kao Poslato (ostaje Poslato i posle sinhronizacije).</div>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <Input type="date" value={cutoff} onChange={e => { setCutoff(e.target.value); setStatus(null); }} style={{ width: 160 }} />
        <Btn variant="secondary" disabled={loading || !cutoff} onClick={handleRun}>{loading ? "Izvršavanje…" : "Označi kao Poslato"}</Btn>
      </div>
      {status && <div style={{ marginTop: 10, fontSize: 12, color: status.startsWith("Završeno") ? "#2a9d5c" : "#E24B4A" }}>{status}</div>}
    </div>
  );
}

// ── Korisnici (User Management) ───────────────────────────────────────────────
const ROLE_LABELS = { admin: "Admin", uprava: "Uprava", proizvodnja: "Proizvodnja" };
const ACTION_LABELS = { created: "kreirao", edited: "izmenio", deleted: "obrisao", cancelled: "otkazao" };

function ActivityLog() {
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    apiFetch(`${API_BASE}/api/activity-log`)
      .then(r => r.json())
      .then(d => { if (d.success) setLog(d.log); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  function exportLog() {
    const rows = log.map(e => ({
      "Korisnik":  e.user,
      "Akcija":    ACTION_LABELS[e.action] || e.action,
      "Tip":       e.type === "direct-order" ? "Direktna porudžbina" : e.type === "expense" ? "Trošak" : e.type === "marketing" ? "Marketing trošak" : e.type,
      "Detalji":   e.details || "",
      "Vreme":     new Date(e.ts).toLocaleString("sr-RS"),
    }));
    downloadXLSX([["Aktivnosti", buildSheet(rows, [16, 14, 22, 45, 20])]], `Aktivnosti_${today()}.xlsx`);
  }

  return (
    <div style={{ background: "#fff", borderRadius: 10, border: "0.5px solid #eee", padding: 20, marginTop: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#444" }}>Aktivnosti</div>
        {log.length > 0 && <Btn size="sm" onClick={exportLog}>⬇ Preuzmi</Btn>}
      </div>
      <div style={{ maxHeight: 340, overflowY: "auto" }}>
      {loading && <div style={{ color: "#aaa", fontSize: 13 }}>Učitavanje…</div>}
      {!loading && log.length === 0 && <div style={{ color: "#aaa", fontSize: 13 }}>Nema aktivnosti.</div>}
      {log.map((e, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "7px 0", borderBottom: "0.5px solid #f0f0f0", fontSize: 13 }}>
          <span>
            <strong>{e.user}</strong>{" "}
            <span style={{ color: "#888" }}>{ACTION_LABELS[e.action] || e.action}{" "}
              {e.type === "direct-order" ? "direktnu porudžbinu" : e.type === "expense" ? "trošak" : e.type === "marketing" ? "marketing trošak" : e.type}
            </span>
            {e.details ? <span style={{ color: "#555" }}> — {e.details}</span> : ""}
          </span>
          <span style={{ color: "#bbb", fontSize: 11, flexShrink: 0, marginLeft: 16 }}>
            {new Date(e.ts).toLocaleString("sr-RS")}
          </span>
        </div>
      ))}
      </div>
    </div>
  );
}

function BackupOrders() {
  const [status, setStatus] = useState("");

  async function handleBackup() {
    setStatus("Preuzimanje…");
    try {
      const [ordersRes, datesRes, expensesRes, mEntriesRes, mChannelsRes, expCatRes] = await Promise.all([
        apiFetch(`${API_BASE}/api/direct-orders`),
        apiFetch(`${API_BASE}/api/sent-dates`),
        apiFetch(`${API_BASE}/api/store/expenses`),
        apiFetch(`${API_BASE}/api/store/marketingEntries`),
        apiFetch(`${API_BASE}/api/store/marketingChannels`),
        apiFetch(`${API_BASE}/api/store/expenseCategories`),
      ]);
      const ordersData = await ordersRes.json();
      const datesData = await datesRes.json();
      const expensesData = await expensesRes.json();
      const mEntriesData = await mEntriesRes.json();
      const mChannelsData = await mChannelsRes.json();
      const expCatData = await expCatRes.json();

      if (!ordersData.success) { setStatus("Greška pri preuzimanju porudžbina."); return; }

      function downloadJson(obj, filename) {
        const blob = new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
      }

      const dt = today();
      downloadJson(ordersData.orders, `direct-orders-${dt}.json`);
      if (datesData.success) downloadJson(datesData.dates, `sent-dates-${dt}.json`);
      if (expensesData.success) downloadJson(expensesData.data, `expenses-${dt}.json`);
      if (mEntriesData.success && mChannelsData.success) {
        downloadJson({ marketingEntries: mEntriesData.data, marketingChannels: mChannelsData.data }, `marketing-${dt}.json`);
      }
      if (expCatData.success) downloadJson(expCatData.data, `expense-categories-${dt}.json`);

      setStatus(`Sačuvano (${ordersData.orders.length} porudžbina).`);
    } catch { setStatus("Greška."); }
  }

  return (
    <div style={{ background: "#fff", borderRadius: 10, border: "0.5px solid #eee", padding: 20, marginTop: 20, color: "#111" }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: "#444" }}>Backup</div>
      <div style={{ fontSize: 12, color: "#888", marginBottom: 12 }}>Preuzmi kopiju porudžbina, isporučeno, troškova i marketinga sa servera pre ažuriranja.</div>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <Btn onClick={handleBackup} style={{ color: "#111" }}>↓ Preuzmi backup</Btn>
        {status && <span style={{ fontSize: 12, color: status.startsWith("Greška") ? "#E24B4A" : "#1D9E75" }}>{status}</span>}
      </div>
    </div>
  );
}

function KorisniciPage() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ username: "", password: "", role: "uprava" });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    apiFetch(`${API_BASE}/api/users`)
      .then(r => r.json())
      .then(d => { if (d.success) setUsers(d.users); });
  }, []);

  async function handleAdd(e) {
    e.preventDefault();
    setError(""); setSuccess(""); setLoading(true);
    try {
      const res = await apiFetch(`${API_BASE}/api/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) {
        setUsers([...users, { username: form.username, role: form.role }]);
        setForm({ username: "", password: "", role: "uprava" });
        setSuccess("Korisnik dodat.");
      } else setError(data.error || "Greška.");
    } catch { setError("Greška pri dodavanju korisnika."); }
    setLoading(false);
  }

  async function handleDelete(username) {
    if (!window.confirm(`Obrisati korisnika "${username}"?`)) return;
    setError(""); setSuccess("");
    try {
      const res = await apiFetch(`${API_BASE}/api/users/${encodeURIComponent(username)}`, { method: "DELETE" });
      const data = await res.json();
      if (data.success) { setUsers(users.filter(u => u.username !== username)); setSuccess("Korisnik obrisan."); }
      else setError(data.error || "Greška.");
    } catch { setError("Greška pri brisanju korisnika."); }
  }

  return (
    <div style={{ padding: 32, maxWidth: 520 }}>
      <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 24 }}>Admin panel</div>

      <div style={{ background: "#fff", borderRadius: 10, border: "0.5px solid #eee", padding: 20, marginBottom: 28, color: "#111" }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 16, color: "#444" }}>Postojeći korisnici</div>
        {users.length === 0 && <div style={{ color: "#aaa", fontSize: 13 }}>Nema korisnika.</div>}
        {users.map(u => (
          <div key={u.username} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderBottom: "0.5px solid #f0f0f0" }}>
            <div>
              <span style={{ fontSize: 14 }}>{u.username}</span>
              <span style={{ fontSize: 11, color: "#aaa", marginLeft: 8 }}>{ROLE_LABELS[u.role] || u.role}</span>
            </div>
            {u.role !== "admin" && (
              <span onClick={() => handleDelete(u.username)} style={{ fontSize: 12, color: "#E24B4A", cursor: "pointer" }}>Obriši</span>
            )}
          </div>
        ))}
      </div>

      <BulkMarkPoslato />

      <BackupOrders />

      <div style={{ background: "#fff", borderRadius: 10, border: "0.5px solid #eee", padding: 20, marginTop: 20, color: "#111" }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 16, color: "#444" }}>Dodaj korisnika</div>
        <form onSubmit={handleAdd}>
          <FormGroup label="Korisničko ime">
            <Input value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} placeholder="npr. marko" />
          </FormGroup>
          <FormGroup label="Lozinka">
            <Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="••••••••" />
          </FormGroup>
          <FormGroup label="Uloga">
            <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}
              style={{ width: "100%", padding: "7px 10px", border: "0.5px solid #ddd", borderRadius: 6, fontSize: 13 }}>
              <option value="uprava">Uprava</option>
              <option value="proizvodnja">Proizvodnja</option>
            </select>
          </FormGroup>
          {error && <div style={{ color: "#E24B4A", fontSize: 12, marginBottom: 10 }}>{error}</div>}
          {success && <div style={{ color: "#2a9d5c", fontSize: 12, marginBottom: 10 }}>{success}</div>}
          <Btn variant="primary" disabled={loading || !form.username || !form.password}>
            {loading ? "Dodavanje…" : "Dodaj korisnika"}
          </Btn>
        </form>
      </div>

      <ActivityLog />

    </div>
  );
}

// ── App Shell ─────────────────────────────────────────────────────────────────

const NAV = [
  { key: "dashboard",  label: "Dashboard" },
  { key: "orders",     label: "Porudžbine" },
  { key: "za-stampu",  label: "Za štampu" },
  { key: "za-slanje",  label: "Isporuka" },
  { key: "expenses",   label: "Troškovi" },
  { key: "crm",        label: "Kupci" },
  { key: "detaljan",   label: "Detaljan pregled" },
  { key: "troskovi",   label: "Troškovi proizvodnje" },
  { key: "podela",     label: "Podela zarade" },
  { key: "korisnici",  label: "Admin panel" },
];

export default function App() {
  const [loggedIn, setLoggedIn] = useState(() => !!getAuthToken());
  const [userRole, setUserRole] = useState(() => getAuthRole());
  const [page, setPage] = useState(() => getAuthRole() === "proizvodnja" ? "za-stampu" : "dashboard");
  const [fromDate, setFromDateRaw] = useState(() => localStorage.getItem("dashFrom") || "2025-03-01");
  const [toDate, setToDateRaw] = useState(() => localStorage.getItem("dashTo") || today());
  function setFromDate(v) { setFromDateRaw(v); localStorage.setItem("dashFrom", v); }
  function setToDate(v) { setToDateRaw(v); localStorage.setItem("dashTo", v); }
  const [rawOrders, setOrders] = useLocalStorage("orders", initialOrders);
  const orders = useMemo(() => rawOrders.map(o => ({
    ...o,
    lines: (o.lines || []).filter(l => (l.price || 0) >= 0 && !DISCOUNT_PRODUCT_NAMES.has(l._rawProduct || l.product)),
  })), [rawOrders]);
  const [contacts, setContacts] = useServerStorage("contacts", initialContacts, loggedIn);
  const allContacts = (() => {
    const emailSet = new Set(contacts.map(c => c.email).filter(Boolean));
    const byEmail = {};
    orders.forEach(o => {
      if (!o.email || emailSet.has(o.email) || byEmail[o.email]) return;
      const parts = (o.customer || "").trim().split(/\s+/);
      byEmail[o.email] = { id: `derived-${o.email}`, first: parts[0] || "", last: parts.slice(1).join(" ") || "", email: o.email, phone: o.phone || "", address: o.city || "", note: "" };
    });
    return [...contacts, ...Object.values(byEmail)];
  })();
  const [expenses, setExpenses] = useServerStorage("expenses", initialExpenses, loggedIn);
  const [marketingEntries, setMarketingEntries] = useServerStorage("marketingEntries", initialMarketingEntries, loggedIn);
  const [marketingChannels, setMarketingChannels] = useServerStorage("marketingChannels", initialMarketingChannels, loggedIn);
  const [expenseCategories, setExpenseCategories] = useServerStorage("expenseCategories", ["Prints", "Packaging", "Shipping", "Other"], loggedIn);
  const [prodCosts, setProdCosts] = useServerStorage("prodCosts", DEFAULT_PROD_COSTS, loggedIn);
  const [purchasePrices, setPurchasePrices] = useServerStorage("purchasePrices", PURCHASE_PRICES, loggedIn);
  const [shippingCfg, setShippingCfg] = useServerStorage("shippingCfg", { shippingCost: 560, freeShippingThreshold: 7000 }, loggedIn);
  const [podelaConfig, setPodelaConfig] = useServerStorage("podelaConfig", DEFAULT_PODELA_CONFIG, loggedIn);
  const [detailOrder, setDetailOrder] = useState(null);
  const [detailOrderBack, setDetailOrderBack] = useState("orders");
  const [editOrder, setEditOrder] = useState(null);
  const [showDirectEdit, setShowDirectEdit] = useState(false);
  const [crmDetailId, setCrmDetailId] = useState(null);
  const [sentDates, setSentDates] = useState({});
  const [ordersReady, setOrdersReady] = useState(false);
  const [zoneOverrides, setZoneOverrides] = useState({});
  const noShippingIds = useMemo(() => new Set(Object.entries(zoneOverrides).filter(([, z]) => z === "samostalno").map(([id]) => id)), [zoneOverrides]);
  const shippingCfgRef = useRef(shippingCfg);
  shippingCfgRef.current = shippingCfg;
  const syncInProgress = useRef(false);
  const knownOrderIdsRef = useRef(null);

  function notifyNewOrder(order) {
    new Audio("/cash-register-.mp3").play().catch(() => {});
    if (!("Notification" in window) || Notification.permission !== "granted") return;
    new Notification("Nova porudžbina! 🛒", {
      body: `#${order.id} — ${order.customer || order.email || ""} — ${(order.total || 0).toLocaleString()} RSD`,
      icon: "/favicon.png",
    });
  }

  useEffect(() => {
    if (!loggedIn) return;
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, [loggedIn]);

  useEffect(() => {
    if (!loggedIn) return;
    Promise.all([fetchWooOrders(), fetchDirectOrders(), fetchSentDates(), fetchZoneOverrides()])
      .then(([{ orders: wooOrders }, serverDirect, dates, zones]) => {
        setZoneOverrides(zones);
        const newDates = { ...dates };
        wooOrders.forEach(o => {
          if (o.status === "poslato" && o.date_completed && !newDates[o.id]) {
            newDates[o.id] = o.date_completed;
            pushSentDate(o.id, o.date_completed);
          }
        });
        setSentDates(newDates);
        apiFetch(`${API_BASE}/api/backfill-sent-dates`, { method: 'POST' })
          .then(r => r.json())
          .then(r => { if (r.success && r.filled > 0) fetchSentDates().then(setSentDates); })
          .catch(() => {});
        setOrders(prev => {
          const threshold = shippingCfgRef.current.freeShippingThreshold;
          const merged = wooOrders.map(o => {
            const existing = prev.find(e => e.id === o.id);
            return { ...o, shippingThreshold: existing?.shippingThreshold ?? threshold };
          });
          const serverIds = new Set(serverDirect.map(o => o.id));
          const localOnly = prev.filter(o => o.type !== "web" && !serverIds.has(o.id));
          localOnly.forEach(o => pushDirectOrder(o));
          const wooIds = new Set(merged.map(o => o.id));
          // Apply any local edits stored in direct-orders.json over the WooCommerce versions,
          // but keep the authoritative status from order-statuses.json (via apply_status_overrides)
          const wooWithOverrides = merged.map(o => { const d = serverDirect.find(s => s.id === o.id); return d ? { ...d, status: o.status } : o; });
          // Entries in direct-orders.json not overlapping WooCommerce (e.g. merged "3590 3593")
          const extraDirect = [...serverDirect.filter(d => !wooIds.has(d.id)), ...localOnly];
          return [...wooWithOverrides, ...extraDirect].sort((a, b) => {
            const dateDiff = b.date.localeCompare(a.date);
            if (dateDiff !== 0) return dateDiff;
            const aTime = a.date_created || String(a.id);
            const bTime = b.date_created || String(b.id);
            return bTime.localeCompare(aTime);
          });
        });
        const lastSeenId = parseInt(localStorage.getItem("lastSeenOrderId") || "0", 10);
        const arrivedWhileAway = wooOrders.filter(o => o.id > lastSeenId);
        if (arrivedWhileAway.length > 0) {
          new Audio("/cash-register-.mp3").play().catch(() => {});
        }
        knownOrderIdsRef.current = new Set(wooOrders.map(o => o.id));
        const maxId = wooOrders.reduce((m, o) => Math.max(m, o.id), lastSeenId);
        localStorage.setItem("lastSeenOrderId", String(maxId));
        setOrdersReady(true);
      }).catch(() => { setOrdersReady(true); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn]);

  async function syncWooOrders() {
    if (syncInProgress.current) return;
    syncInProgress.current = true;
    try {
      const [syncRes, directOrders] = await Promise.all([
        apiFetch(`${API_BASE}/api/sync-woo`, { method: "POST" }).then(r => r.json()),
        fetchDirectOrders(),
      ]);
      if (syncRes.success) {
        const { orders: normalized } = syncRes.delta.length > 0 ? normalizeWooOrders(syncRes.delta) : { orders: [] };
        if (knownOrderIdsRef.current !== null) {
          normalized.forEach(o => {
            if (!knownOrderIdsRef.current.has(o.id)) {
              notifyNewOrder(o);
              knownOrderIdsRef.current.add(o.id);
            }
          });
          const maxId = normalized.reduce((m, o) => Math.max(m, o.id), 0);
          if (maxId > 0) {
            const stored = parseInt(localStorage.getItem("lastSeenOrderId") || "0", 10);
            localStorage.setItem("lastSeenOrderId", String(Math.max(stored, maxId)));
          }
        }
        normalized.forEach(o => {
          if (o.status === "poslato" && o.date_completed) {
            setSentDates(prev => {
              if (prev[o.id]) return prev;
              pushSentDate(o.id, o.date_completed);
              return { ...prev, [o.id]: o.date_completed };
            });
          }
        });
        setOrders(prev => {
          const threshold = shippingCfgRef.current.freeShippingThreshold;
          let next = [...prev];
          normalized.forEach(o => {
            const idx = next.findIndex(x => x.id === o.id);
            if (idx >= 0) next[idx] = { ...next[idx], ...o };
            else next.push({ ...o, shippingThreshold: threshold });
          });
          next = next.map(o => { if (o.type !== "web") return o; const d = directOrders.find(x => x.id === o.id); return d ? { ...d, status: o.status } : o; });
          const serverDirectIds = new Set(directOrders.map(d => d.id));
          next.filter(o => o.type !== "web" && !serverDirectIds.has(o.id)).forEach(o => pushDirectOrder(o));
          const allIds = new Set(next.map(o => o.id));
          directOrders.forEach(d => { if (!allIds.has(d.id)) next.push(d); });
          return next.sort((a, b) => {
            const dateDiff = b.date.localeCompare(a.date);
            if (dateDiff !== 0) return dateDiff;
            const aTime = a.date_created || String(a.id);
            const bTime = b.date_created || String(b.id);
            return bTime.localeCompare(aTime);
          });
        });
      }
    } catch (_) {}
    syncInProgress.current = false;
  }

  useEffect(() => {
    if (!loggedIn) return;
    const id = setInterval(syncWooOrders, 90000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn]);

  useEffect(() => {
    if (!loggedIn) return;
    const handler = () => { if (document.visibilityState === "visible") syncWooOrders(); };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn]);

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const activeNav = page.startsWith("order") ? "orders" : page.startsWith("crm") ? "crm" : page;

  function navigate(key) {
    setPage(key);
    if (isMobile) setSidebarOpen(false);
  }

  if (!loggedIn) return <LoginScreen onLogin={() => {
    const role = getAuthRole();
    setLoggedIn(true);
    setUserRole(role);
    if (role === "proizvodnja") setPage("za-stampu");
  }} />;

  return (
    <div style={{ display: "flex", height: "100vh", fontFamily: "system-ui, sans-serif", background: brand.bgColor }}>

      {isMobile && sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 199 }} />
      )}

      <div style={{ width: 210, minWidth: 210, background: brand.sidebarBg, borderRight: brand.sidebarBorder, display: "flex", flexDirection: "column", padding: "16px 0", overflowY: "auto", ...(isMobile ? { position: "fixed", top: 0, left: 0, bottom: 0, zIndex: 200, transform: sidebarOpen ? "translateX(0)" : "translateX(-100%)", transition: "transform 0.22s ease", boxShadow: sidebarOpen ? "4px 0 24px rgba(0,0,0,0.12)" : "none" } : {}) }}>
        <div style={{ padding: "0 16px 20px", borderBottom: brand.sidebarBorder, marginBottom: 8 }}>
          <img src={brand.logo} alt={brand.logoAlt} style={{ width: "100%", display: "block" }} />
        </div>
        {NAV.filter(n => {
          if (userRole === "admin") return true;
          if (userRole === "uprava") return n.key !== "korisnici";
          if (userRole === "proizvodnja") return ["za-stampu", "za-slanje"].includes(n.key);
          return false;
        }).map(n => (
          <div key={n.key} onClick={() => navigate(n.key)}
            style={{ padding: "9px 16px", cursor: "pointer", fontSize: 13, color: activeNav === n.key ? brand.navActiveColor : brand.navColor, borderLeft: activeNav === n.key ? `2px solid ${brand.navActiveColor}` : "2px solid transparent", background: activeNav === n.key ? brand.navActiveBg : "transparent", fontWeight: activeNav === n.key ? 500 : 400, lineHeight: 1.4 }}>
            {n.label}
          </div>
        ))}
        <div style={{ marginTop: "auto", padding: "16px" }}>
          <div onClick={() => { clearAuthToken(); setLoggedIn(false); setUserRole("user"); }} style={{ fontSize: 12, color: brand.logoutColor, cursor: "pointer" }}>Odjavi se</div>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: isMobile ? 16 : 24, color: brand.textColor }}>
        {isMobile && (
          <button onClick={() => setSidebarOpen(true)} style={{ marginBottom: 16, background: "none", border: "none", fontSize: 22, cursor: "pointer", padding: "4px 6px", color: brand.textColor, display: "block" }}>☰</button>
        )}
        {page === "dashboard"    && <Dashboard orders={orders} expenses={expenses} marketingEntries={marketingEntries} prodCosts={prodCosts} purchasePrices={purchasePrices} fromDate={fromDate} toDate={toDate} setFromDate={setFromDate} setToDate={setToDate} shippingCfg={shippingCfg} noShippingIds={noShippingIds} />}
        {page === "orders"       && <Orders orders={orders} setOrders={setOrders} contacts={contacts} setContacts={setContacts} setPage={setPage} setDetailOrder={setDetailOrder} setDetailOrderBack={setDetailOrderBack} prodCosts={prodCosts} purchasePrices={purchasePrices} marketingEntries={marketingEntries} shippingCfg={shippingCfg} noShippingIds={noShippingIds} />}
        {page === "order-detail" && <OrderDetail order={detailOrder} onBack={() => setPage(detailOrderBack)}
            onEdit={() => { setEditOrder(detailOrder); setShowDirectEdit(true); }}
            onComplete={() => { const upd = { ...detailOrder, status: "completed" }; setOrders(orders.map(o => o.id === detailOrder.id ? upd : o)); setDetailOrder(upd); if (detailOrder.type !== "web") pushDirectOrder(upd); else pushStatusToServer(detailOrder.id, "completed"); }}
            onCancel={() => { const upd = { ...detailOrder, status: "cancelled" }; setOrders(orders.map(o => o.id === detailOrder.id ? upd : o)); setDetailOrder(upd); if (detailOrder.type !== "web") pushDirectOrder(upd); else pushStatusToServer(detailOrder.id, "cancelled"); }}
            onUncancel={() => { const upd = { ...detailOrder, status: "pending" }; setOrders(orders.map(o => o.id === detailOrder.id ? upd : o)); setDetailOrder(upd); if (detailOrder.type !== "web") pushDirectOrder(upd); else pushStatusToServer(detailOrder.id, "pending"); }}
            prodCosts={prodCosts} purchasePrices={purchasePrices} marketingEntries={marketingEntries} allOrders={orders} shippingCfg={shippingCfg} noShippingIds={noShippingIds} />}
        {page === "za-stampu"    && <ZaStampu orders={orders} setOrders={setOrders} ordersReady={ordersReady} />}
        {page === "za-slanje"    && <ZaSlanje orders={orders} setOrders={setOrders} contacts={contacts} sentDates={sentDates} setSentDates={setSentDates} zoneOverrides={zoneOverrides} setZoneOverrides={setZoneOverrides} />}
        {page === "expenses"     && <Expenses expenses={expenses} setExpenses={setExpenses} marketingEntries={marketingEntries} setMarketingEntries={setMarketingEntries} marketingChannels={marketingChannels} setMarketingChannels={setMarketingChannels} expenseCategories={expenseCategories} setExpenseCategories={setExpenseCategories} fromDate={fromDate} toDate={toDate} setFromDate={setFromDate} setToDate={setToDate} shippingCfg={shippingCfg} setShippingCfg={setShippingCfg} />}
        {page === "crm"          && <CRM contacts={allContacts} setContacts={setContacts} orders={orders} onViewContact={id => { setCrmDetailId(id); setPage("crm-detail"); }} />}
        {page === "crm-detail"   && <CRMDetail contactId={crmDetailId} contacts={allContacts} setContacts={setContacts} orders={orders} onBack={() => setPage("crm")} onViewOrder={o => { setDetailOrder(o); setDetailOrderBack("crm-detail"); setPage("order-detail"); }} />}
        {page === "detaljan"     && <DetaljanPregled orders={orders} prodCosts={prodCosts} purchasePrices={purchasePrices} marketingEntries={marketingEntries} fromDate={fromDate} toDate={toDate} setFromDate={setFromDate} setToDate={setToDate} shippingCfg={shippingCfg} noShippingIds={noShippingIds} />}
        {page === "troskovi"     && <TroskoviProizvodnje prodCosts={prodCosts} setProdCosts={setProdCosts} purchasePrices={purchasePrices} setPurchasePrices={setPurchasePrices} />}
        {page === "podela"       && <PodelaZarade orders={orders} expenses={expenses} marketingEntries={marketingEntries} prodCosts={prodCosts} purchasePrices={purchasePrices} shippingCfg={shippingCfg} noShippingIds={noShippingIds} fromDate={fromDate} toDate={toDate} setFromDate={setFromDate} setToDate={setToDate} config={podelaConfig} setConfig={setPodelaConfig} />}
        {page === "korisnici"    && <KorisniciPage />}
      </div>

      <DirectSaleModal open={showDirectEdit} onClose={() => { setShowDirectEdit(false); setEditOrder(null); }}
        onSave={data => { const upd = { ...editOrder, ...data }; setOrders(orders.map(o => o.id === editOrder.id ? upd : o)); setDetailOrder({ ...detailOrder, ...data }); pushDirectOrder(upd); }}
        contacts={contacts} setContacts={setContacts} editOrder={editOrder} orders={orders} />
    </div>
  );
}